import {useColorTheme} from './color-theme.jsx';
import {
  PALETTE,
  NEUTRAL,
  SEMANTIC,
  HEAT_RAMP,
  CursorTip,
  fmtN,
  pieCenterX,
  ChartCard,
  ChartEmpty,
  MultiSeriesTooltip,
  hasData,
  useContainerWidth,
  resolveFormatter,
  monotonePath,
  tintColor,
  thinLabelIndices,
  xLabelStride,
  SlotLabel,
  peakLabelIndices,
  legendLayout,
  SVGLegendRows,
  // Rule 1 plumbing (single implementation in shared.jsx): labels ON
  // by default, editor `dataLabels` spec always wins, `showValues`
  // prop is the opt-out.
  resolveLabels,
  lastDelta,
  // IndexMeter delta chip (Pricing-Brand G-3): the semantic ▲/▼ pill
  // and its sentiment resolver, shared with the KPI cards.
  DeltaChip,
  resolveSentiment,
  // Axis polish 2026-07: round tick values + label anti-collision +
  // density-adaptive solid line markers (style "C", no white halo).
  niceTicks,
  niceSymmetricTicks,
  separateLabelYs,
  lineDotRadius,
  // Adaptive label gutter + 2-line wrap for ranked horizontal bars
  // (feedback 2026-07-14) — single implementation in shared.jsx.
  barLabelLayout,
  // Densidad de las barras rankeadas — una sola métrica para la
  // variante estática y la interactiva (antes duplicada en las dos).
  barDensity,
} from './shared.jsx';
import { useId, useMemo, useRef, useState } from 'react';
import { useGrowCycle, lineDataSig, MOTION, useEntrance, usePrefersReducedMotion, EntranceGroup, useLineDraw } from './motion.jsx';
import { useTooltip, ChartSVGWrap } from './charts-interactive.jsx';
export { ChartCard, PALETTE };

// All chart components measure their actual container width via ResizeObserver
// so they fill whatever container they're placed in — no more empty side gaps.
// `width` prop is now an optional SSR/initial-render hint (default 400).

// ── Helpers ──────────────────────────────────────────────────
//
// `safeMax(arr)` returns the max of an array, but never zero, never
// negative-infinity (empty arr) and never -Infinity. Used everywhere
// scales are computed as `value / max` so a dataset of all-zero rows
// (common when an upstream filter narrows the result set to nothing
// numerically) doesn't poison `<polyline points="NaN,30">` and crash
// SVG rendering.
function safeMax(arr) {
  let m = -Infinity;
  for (const v of arr) {
    if (typeof v === 'number' && Number.isFinite(v) && v > m) m = v;
  }
  return Number.isFinite(m) && m > 0 ? m : 1;
}

// Same idea for min — used by waterfall / line / bar_trend where the
// scale spans negative + positive values. Default falls to 0 so the
// lower bound just touches the baseline.
function safeMin(arr) {
  let m = Infinity;
  for (const v of arr) {
    if (typeof v === 'number' && Number.isFinite(v) && v < m) m = v;
  }
  return Number.isFinite(m) ? m : 0;
}

// `xRatio(i, n)` returns the proportional X position [0..1] for index
// `i` of `n` total points. Guards against a single-point series where
// `i / (n - 1)` would divide by zero — single points get placed at
// the centre of the chart instead. Use whenever building polyline /
// polygon / line / text X coords from an index over a series length.
function xRatio(i, n) {
  if (n <= 1) return 0.5;
  return i / (n - 1);
}

// Line charts keep their first and last points LINE_INSET px inside the
// plot, so a marker never sits on the Y-axis labels (Evolución,
// 2026-10-26: the first point of every retailer covered the "20" tick).
const LINE_INSET = 10;
function lineX(i, n, left, width) {
  return left + LINE_INSET + xRatio(i, n) * Math.max(0, width - 2 * LINE_INSET);
}

// ── Vertical Bar ────────────────────────────────────────────
// Auto-detects when bars would be too narrow (< MIN_COL_W px each) and
// expands the SVG to natural width, enabling horizontal scroll on the wrapper.
export function VerticalBar({ data, color, height = 200, width: widthHint = 400, dataLabels, showValues = true, axisYFormat, entrance = true, ariaLabel = 'Gráfico de barras verticales' }) {
  const componentTheme = useColorTheme('VerticalBar');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Unique per instance (audit #211): `url(#id)` resolves to the FIRST match
  // in the document, so a fixed id makes a 2nd chart borrow the 1st one's.
  const gid = useId().replace(/[^\w-]/g, '');
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  // Round axis ticks; the scale max IS the top tick (≥ data max).
  const { ticks: yTicks, max } = niceTicks(safeMax(data.map(d => d.value)));
  const h = height, pad = { l: 48, r: 12, t: 14, b: 36 };
  const n = data.length;
  const MIN_COL_W = 32;
  const naturalW = Math.max(w, pad.l + pad.r + Math.ceil(n * MIN_COL_W / 0.65));
  const scrollX = naturalW > w;
  const cw = naturalW - pad.l - pad.r, ch = h - pad.t - pad.b;
  const bw = Math.min(cw / n * 0.65, 40);
  const rx = Math.min(6, bw / 2);
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  // Rule 1: labels ON by default, bold, auto-thinned when dense.
  const labelFmt = resolveLabels(dataLabels, showValues);
  const labelIdx = labelFmt ? thinLabelIndices(data.map(d => d.value)) : null;
  return (
    <div ref={wrapRef} style={{ height: h, overflowX: scrollX ? 'auto' : 'hidden', overflowY: 'hidden' }} className="w-full">
      <svg width={naturalW} height={h} role="img" aria-label={ariaLabel}>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.l} x2={naturalW - pad.r} y1={pad.t + ch * (1 - v / max)} y2={pad.t + ch * (1 - v / max)} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l - 6} y={pad.t + ch * (1 - v / max) + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text>
          </g>
        ))}
        {data.map((d, i) => {
          const x = pad.l + (i + 0.5) * (cw / n) - bw / 2, bh = (d.value / max) * ch;
          return (
            <g key={i} className="group cursor-pointer">
              <defs><linearGradient id={`${gid}-vb${i}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.95"/><stop offset="100%" stopColor={color} stopOpacity="0.6"/></linearGradient></defs>
              <EntranceGroup mode="growY" origin={`0px ${pad.t + ch}px`} disabled={!entrance} drawn={chartDrawn}>
                <rect x={x} y={pad.t + ch - bh} width={bw} height={bh} fill={`url(#${gid}-vb${i})`} rx={rx} className="transition-opacity group-hover:opacity-75"/>
              </EntranceGroup>
              {labelFmt && labelIdx.has(i) && <text x={x + bw / 2} y={pad.t + ch - bh - 4} textAnchor="middle" fontSize="11" fontWeight="700" fill={NEUTRAL.text}>{labelFmt(d.value)}</text>}
              <SlotLabel label={d.label} slot={cw / n} x={x + bw / 2} y={h - pad.b + 16} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint} />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Horizontal Bar ───────────────────────────────────────────
// Redesign rule 5 — "ranked style" (mirror of `HorizontalBarT` minus
// hover): numbered rank chip + row label + pill-shaped track with a
// tint degradé of the base color per rank + bold value + share-of-
// total %. Rows render in the order given — never re-sorted.
// Auto-detects when rows would be too thin (< MIN_ROW_H px each) and
// expands the SVG to natural height, enabling vertical scroll on the wrapper.
export function HorizontalBar({ data, color, height = 200, width: widthHint = 400, dataLabels, showValues = true, showShare = true, format = v => v.toLocaleString(), density = 'comfortable', entrance = true, ariaLabel = 'Gráfico de barras horizontales' }) {
  const componentTheme = useColorTheme('HorizontalBar');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[1 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[1 % clientColors.length];

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const max = safeMax(data.map(d => d.value));
  const total = data.reduce((s, d) => s + (typeof d.value === 'number' && Number.isFinite(d.value) ? d.value : 0), 0);
  const D = barDensity(density);
  const CHIP_W = D.chipW, CHIP_GAP = D.chipGap, LABEL_GAP = 8;
  // Adaptive label channel (2026-07-14 feedback): grows with the
  // longest label up to ~38% of the chart width; labels that still
  // don't fit wrap to two <tspan> lines (see barLabelLayout). En
  // compacto el chip no existe, así que sus 28px vuelven al canal.
  const labelMaxPx = Math.max(64, Math.round(w * 0.38) - CHIP_W - CHIP_GAP - LABEL_GAP);
  const { rows: labelRows, textW: labelW } = barLabelLayout(data.map(d => d.label), labelMaxPx, 62, D.rowH >= 24);
  const padL = CHIP_W + CHIP_GAP + labelW + LABEL_GAP;
  const labelFmt = resolveLabels(dataLabels, showValues, format);
  const n = data.length;
  const naturalH = Math.max(height, 16 + n * D.rowH); // 16 = pad.t + pad.b
  const scrollY = naturalH > height;
  // Right column auto-grow (redesign feedback r2): measure the widest
  // formatted value + share % and reserve exactly that much, plus a
  // scrollbar allowance when the chart scrolls vertically — a classic
  // (non-overlay) scrollbar overlays ~17px of the SVG's right edge,
  // which used to clip the last characters of the value / %.
  const SB_W = scrollY ? 17 : 0;
  const EDGE = 6, VAL_GAP = 8, TRACK_GAP = 8;
  // Share-of-total: solo cuando los valores SON partes de un todo
  // (conteos, montos). Para un indice (mercado = 100), un precio
  // promedio, un ratio o un puntaje, la magnitud no es aditiva y ese
  // porcentaje no significa nada: en el Inspector competidor cada
  // marca mostraba su indice "72" con un "6.4%" al lado que no es
  // ninguna cantidad del mundo. Por eso se puede apagar — y sigue
  // encendido por defecto, que es lo que esperan los rankeados de
  // conteos que ya existen.
  const shareTxts = showShare
    ? data.map(d => (total > 0 && Number.isFinite(d.value) ? `${((d.value / total) * 100).toFixed(1)}%` : null))
    : data.map(() => null);
  const valueW = labelFmt ? Math.max(...data.map(d => String(labelFmt(d.value)).length)) * D.valueCharPx : 0;
  const shareW = labelFmt && shareTxts.some(s => s != null)
    ? Math.max(...shareTxts.filter(s => s != null).map(s => s.length)) * D.shareCharPx : 0;
  const padR = labelFmt
    ? Math.ceil(TRACK_GAP + valueW + (shareW ? VAL_GAP + shareW : 0) + EDGE) + SB_W
    : 16 + SB_W;
  const shareX = w - SB_W - EDGE;                             // right edge of the share %
  const valueX = shareW ? shareX - shareW - VAL_GAP : shareX; // right edge of the bold value
  const pad = { l: padL, r: padR, t: 8, b: 8 };
  const cw = w - pad.l - pad.r, ch = naturalH - pad.t - pad.b;
  const bh = Math.min(ch / n * 0.6, D.barMax);
  const rx = Math.min(9, bh / 2);
  return (
    <div ref={wrapRef} className="w-full overflow-x-hidden" style={{ height, overflowY: scrollY ? 'auto' : 'hidden' }}>
      <svg width={w} height={naturalH} role="img" aria-label={ariaLabel}>
        {data.map((d, i) => {
          const rowC = pad.t + (i + 0.5) * (ch / n), y = rowC - bh / 2;
          const bw = Math.max(4, (d.value / max) * cw);
          // Rank degradé: base color for #1, tinting toward white as
          // rank drops (rule 5) — single brand color, no hex ramp.
          const fill = tintColor(color, n <= 1 ? 0 : (i / (n - 1)) * 0.72);
          return (
            <g key={i} className="group cursor-pointer">
              {/* Rank chip — en compacto NO se dibuja: el orden de las
                  filas ya ES el ranking, y sus 28px se los devuelve al
                  canal de labels. */}
              {CHIP_W > 0 && (
                <>
                  <rect x={0} y={rowC - CHIP_W / 2} width={CHIP_W} height={CHIP_W} rx="6" fill={NEUTRAL.track}/>
                  <text x={CHIP_W / 2} y={rowC + 3.5} textAnchor="middle" fontSize={D.chipFs} fontWeight="800" fill={NEUTRAL.muted}>{i + 1}</text>
                </>
              )}
              {/* Row label — wraps to two end-anchored tspan lines when
                  it exceeds the adaptive channel; ellipsized line 2
                  carries the full label in a native <title>. */}
              {labelRows[i].wrapped ? (
                <text x={pad.l - LABEL_GAP} y={rowC - 2.5} textAnchor="end" fontSize={D.wrapFs} fill={NEUTRAL.text} fontWeight="600">
                  {labelRows[i].truncated && <title>{labelRows[i].full}</title>}
                  <tspan x={pad.l - LABEL_GAP} dy="0">{labelRows[i].lines[0]}</tspan>
                  <tspan x={pad.l - LABEL_GAP} dy={D.wrapDy}>{labelRows[i].lines[1]}</tspan>
                </text>
              ) : (
                <text x={pad.l - LABEL_GAP} y={rowC + 3.5} textAnchor="end" fontSize={D.labelFs} fill={NEUTRAL.text} fontWeight="600">{labelRows[i].lines[0]}</text>
              )}
              {/* Pill track + ranked pill bar */}
              <rect x={pad.l} y={y} width={cw} height={bh} fill={NEUTRAL.track} rx={rx}/>
              <EntranceGroup mode="growX" origin={`${pad.l}px 0px`} disabled={!entrance} drawn={chartDrawn}>
                <rect x={pad.l} y={y} width={bw} height={bh} fill={fill} rx={rx} opacity="0.92" className="transition-opacity group-hover:opacity-100"/>
              </EntranceGroup>
              {/* Bold value + share of total */}
              {labelFmt && <text x={valueX} y={rowC + 3.5} textAnchor="end" fontSize={D.valueFs} fill={NEUTRAL.text} fontWeight="700">{labelFmt(d.value)}</text>}
              {labelFmt && shareTxts[i] != null && <text x={shareX} y={rowC + 3.5} textAnchor="end" fontSize={D.shareFs} fill={NEUTRAL.muted} fontWeight="600">{shareTxts[i]}</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Stacked Bar ──────────────────────────────────────────────
// Rule 1: total labels above the columns (thinned) + % painted
// inside segments that carry ≥ 10% of the column. Rule 3: the bare
// swatch legend is now a legend-table (swatch + key + last value +
// delta chip vs the previous row).
export function StackedBar({ data, keys, colors, height = 200, width: widthHint = 400, dataLabels, showValues = true, axisYFormat, entrance = true, ariaLabel = 'Barras apiladas' }) {
  const componentTheme = useColorTheme('StackedBar');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) colors = componentTheme.palette;
  if (colors === undefined) colors = clientColors;

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const totals = data.map(d => keys.reduce((s, k) => s + (d[k] || 0), 0));
  // Round axis ticks; the scale max IS the top tick (≥ data max).
  const { ticks: yTicks, max } = niceTicks(safeMax(totals));
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  const labelFmt = resolveLabels(dataLabels, showValues);
  const totalLabelIdx = labelFmt ? thinLabelIndices(totals) : null;
  const legendItems = keys.map((k, ki) => {
    const { value, delta } = lastDelta(data.map(d => d[k] || 0));
    return { name: k, color: colors[ki % colors.length], value: value != null ? yAxisFmt(value) : null, delta };
  });
  const lgd = legendLayout(legendItems, Math.max(120, w - 16));
  const h = height, pad = { l: 48, r: 12, t: 14, b: 30 + lgd.height };
  const cw = w - pad.l - pad.r, ch = h - pad.t - pad.b, n = data.length;
  const bw = Math.min(cw / n * 0.6, 36);
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.l} x2={w - pad.r} y1={pad.t + ch * (1 - v / max)} y2={pad.t + ch * (1 - v / max)} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l - 6} y={pad.t + ch * (1 - v / max) + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text>
          </g>
        ))}
        {data.map((d, i) => {
          const x = pad.l + (i + 0.5) * (cw / n) - bw / 2; let cumY = 0;
          const total = totals[i];
          const totalH = (total / max) * ch;
          return (
            <g key={i}>
              {keys.map((k, ki) => { const val = d[k] || 0, segH = (val / max) * ch, y = pad.t + ch - cumY - segH; cumY += segH;
                const share = total > 0 ? val / total : 0;
                return (
                  <g key={k}>
                    <EntranceGroup mode="growY" origin={`0px ${pad.t + ch}px`} disabled={!entrance} drawn={chartDrawn}>
                      <rect x={x} y={y} width={bw} height={segH} fill={colors[ki % colors.length]} rx={ki === keys.length - 1 ? 3 : 0} opacity="0.85"/>
                    </EntranceGroup>
                    {/* Rule 1: % inside segments ≥ 10% of the column */}
                    {labelFmt && share >= 0.1 && segH >= 12 && bw >= 22 && (
                      <text x={x + bw / 2} y={y + segH / 2 + 3.5} textAnchor="middle" fontSize="11" fontWeight="800" fill="white" pointerEvents="none">{Math.round(share * 100)}%</text>
                    )}
                  </g>
                );
              })}
              {labelFmt && totalH > 0 && totalLabelIdx.has(i) && <text x={x + bw / 2} y={pad.t + ch - totalH - 4} textAnchor="middle" fontSize="11" fontWeight="700" fill={NEUTRAL.text}>{labelFmt(total)}</text>}
              <SlotLabel label={d.label} slot={cw / n} x={x + bw / 2} y={pad.t + ch + 16} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint} />
            </g>
          );
        })}
        {/* Legend-table (rule 3) */}
        <SVGLegendRows layout={lgd} width={w} top={h - lgd.height} />
      </svg>
    </div>
  );
}

// ── Diverging Stacked Bar ────────────────────────────────────
//
// Redesign v2 (2026-07): aggregated flow view. The inflow TOTAL per
// column renders above the zero axis in the first palette color
// (magenta) and the outflow TOTAL below in the second (indigo), plus
// an optional monotone-cubic net line (dark, with dots). Direction is
// encoded by position + sign — NOT by red/green — an explicit product
// decision for this chart (see design_tmp/redesign_plan.md).
//
// Input shape is unchanged from v1 (per-key breakdown preserved):
//   data = [{label: '2025-04-21', in: {YES: 5, NO: 12}, out: {YES: 3, NO: 8}}]
//   keys = ['YES', 'NO', '1P', 'HHO']
//
// The per-key detail (up to 2×N series) surfaces ONLY through the
// MultiSeriesTooltip on hover — never as N bar colors.
//
// Scale: one absolute px-per-unit anchored at 0 for both halves
// (``maxAbs``), so a +X bar measures identically to a −X bar. Earlier
// versions scaled each half to its own peak, which made the zero
// baseline read as tilted (asymmetric tick spacing illusion); gating
// users compare inflow vs outflow magnitudes week-to-week, so equal
// pixels per unit is the property that matters.
//
// Peaks are annotated automatically: top-2 |value| per sign (rule 1
// of the redesign), formatted with the ``dataLabels`` spec when one
// is provided; ``showValues={false}`` (or ``dataLabels.show: false``)
// hides the annotations.
export function DivergingStackedBar({
  data,
  keys,
  colors = PALETTE,
  height = 240,
  width: widthHint = 400,
  axisYFormat,
  dataLabels,
  showValues = true,
  // Dark monotone net line (inflow − outflow) with dots.
  netLine = true,
  ariaLabel = 'Flujos por MPA (entradas / salidas)',
  // i18n labels for the legend + tooltip. The consumer (`GenericChart`)
  // localises these via the active i18n locale.
  inflowLabel = 'Inflow',
  outflowLabel = 'Outflow',
  netLabel = 'Net',
  entrance = true,
}) {
  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  const { tip, svgRef, show, hide } = useTooltip();
  // Entrance: solid net line draws in; hooks stay unconditional (top).
  const netDraw = useLineDraw(!entrance, lineDataSig(data));
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const inTotals = data.map(d => keys.reduce((s, k) => s + ((d.in?.[k]) || 0), 0));
  const outTotals = data.map(d => keys.reduce((s, k) => s + ((d.out?.[k]) || 0), 0));
  const nets = inTotals.map((v, i) => v - outTotals[i]);
  // Round symmetric ticks (…, −1K, 0, 1K, …); the shared scale max IS
  // the top tick (≥ the larger of the two half-plane data maxima).
  const { ticks: yTicks, max: maxAbs } = niceSymmetricTicks(
    Math.max(safeMax(inTotals), safeMax(outTotals)), 3,
  );
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  // Labels ON by default (redesign rule 1); editor spec wins.
  const labelFmt = resolveLabels(dataLabels, showValues);

  const inColor = colors[0 % colors.length];
  const outColor = colors[1 % colors.length];
  const netColor = NEUTRAL.text;

  // Auto-grow left padding so Y axis labels don't bleed into the
  // chart body. Negative tick labels carry a sign character, so
  // measure the actual rendered values.
  const yLabelMax = Math.max(
    ...yTicks.map(v => String(yAxisFmt(v)).length),
  );
  const padL = Math.max(44, Math.min(96, yLabelMax * 7 + 12));

  // Legend: 2 aggregated entries (+ net) — never 2×N key rows.
  const legendItems = [
    { id: 'in', shape: 'rect', color: inColor, label: `${inflowLabel} (+)` },
    { id: 'out', shape: 'rect', color: outColor, label: `${outflowLabel} (−)` },
    ...(netLine ? [{ id: 'net', shape: 'line', color: netColor, label: netLabel }] : []),
  ].map(item => ({ ...item, w: Math.ceil(item.label.length * 6) + 24 }));
  const LEGEND_H = 22;
  const h = height, pad = { l: padL, r: 12, t: 16, b: 30 + LEGEND_H };
  const cw = w - pad.l - pad.r, ch = h - pad.t - pad.b, n = data.length;
  const colW = cw / n;
  const bw = Math.min(colW * 0.62, 26);
  // Rounded pill-like bars (rule 6: bar rx 5→6, approved reference).
  const rx = Math.min(6, bw / 3);
  const yZero = pad.t + ch / 2;
  const half = ch / 2;
  const labelEvery = xLabelStride(data.map(d => d.label), cw, 8);

  // Net line geometry (shared scale, sign decides the half plane).
  const netPts = nets.map((v, i) => ({
    x: pad.l + (i + 0.5) * colW,
    y: yZero - (v / maxAbs) * half,
  }));

  // Peak annotations: top-2 per sign among the aggregated totals.
  const topIdx = (vals) => vals
    .map((v, i) => [v, i])
    .filter(([v]) => typeof v === 'number' && Number.isFinite(v) && v > 0)
    .sort((a, b) => b[0] - a[0])
    .slice(0, 2)
    .map(([, i]) => i);
  const inPeaks = labelFmt ? new Set(topIdx(inTotals)) : new Set();
  const outPeaks = labelFmt ? new Set(topIdx(outTotals)) : new Set();

  // Per-week tooltip: aggregated totals + the per-key breakdown (the
  // ONLY surface where the 2×N series detail lives) + net.
  // A single key IS the half: "Caídas (+)", not "Caídas FLOW (+)".
  const keyTag = (k) => (keys.length === 1 ? '' : ` ${k}`);
  const tooltipFor = (idx) => {
    const d = data[idx];
    const series = [
      ...keys.map((k) => ({
        name: `${inflowLabel}${keyTag(k)} (+)`,
        value: (d.in?.[k]) || 0,
        color: inColor,
      })),
      ...keys.map((k) => ({
        name: `${outflowLabel}${keyTag(k)} (−)`,
        value: (d.out?.[k]) || 0,
        color: outColor,
      })),
      ...(netLine ? [{ name: netLabel, value: nets[idx], color: netColor }] : []),
    ];
    return (
      <MultiSeriesTooltip
        label={d.label}
        series={series}
        valueFmt={(v) => yAxisFmt(v)}
        showTotal={false}
      />
    );
  };

  const legendW = legendItems.reduce((s, it) => s + it.w, 0);
  let legendX = Math.max(0, (w - legendW) / 2);

  return (
    <ChartSVGWrap tip={tip} svgRef={svgRef} wrapRef={wrapRef} height={h}>
      <svg ref={svgRef} width={w} height={h} role="img" aria-label={ariaLabel}>
        {/* Y grid: round symmetric ticks mirrored across zero. The zero
            baseline is drawn explicitly (approved widget reference) —
            safe now that both halves share one scale. */}
        {yTicks.map((value) => {
          const y = yZero - (value / maxAbs) * half;
          const isZero = value === 0;
          return (
            <g key={value}>
              <line x1={pad.l} x2={w - pad.r} y1={y} y2={y}
                    stroke={isZero ? NEUTRAL.axis : NEUTRAL.grid}
                    strokeWidth={isZero ? 1.4 : 0.8}/>
              <text x={pad.l - 6} y={y + 4} textAnchor="end" fontSize="11"
                    fill={isZero ? NEUTRAL.text : NEUTRAL.faint}
                    fontWeight={isZero ? '600' : '400'}>
                {yAxisFmt(value)}
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const xCenter = pad.l + (i + 0.5) * colW;
          const x = xCenter - bw / 2;
          const inV = inTotals[i], outV = outTotals[i];
          const hU = inV > 0 ? Math.max(2, (inV / maxAbs) * half) : 0;
          const hD = outV > 0 ? Math.max(2, (outV / maxAbs) * half) : 0;
          return (
            <g key={i}>
              {/* Inflow total (above zero, magenta) */}
              {hU > 0 && (
                <EntranceGroup mode="growY" origin={`0px ${yZero}px`} disabled={!entrance} drawn={chartDrawn}>
                  <rect x={x} y={yZero - hU} width={bw} height={hU} rx={rx} fill={inColor} opacity="0.95"/>
                </EntranceGroup>
              )}
              {/* Outflow total (below zero, indigo) */}
              {hD > 0 && (
                <EntranceGroup mode="growY" origin={`0px ${yZero}px`} disabled={!entrance} drawn={chartDrawn}>
                  <rect x={x} y={yZero} width={bw} height={hD} rx={rx} fill={outColor} opacity="0.88"/>
                </EntranceGroup>
              )}
              {/* Automatic peak annotations: top-2 |value| per sign */}
              {inPeaks.has(i) && (
                <text x={xCenter} y={yZero - hU - 6} textAnchor="middle" fontSize="11"
                      fontWeight="800" fill={inColor}>{`+${labelFmt(inV)}`}</text>
              )}
              {outPeaks.has(i) && (
                <text x={xCenter} y={yZero + hD + 13} textAnchor="middle" fontSize="11"
                      fontWeight="800" fill={outColor}>{`−${labelFmt(outV)}`}</text>
              )}
              {/* X tick label — every Nth so they don't overlap */}
              {i % labelEvery === 0 && (
                <text x={xCenter} y={pad.t + ch + 16} textAnchor="middle"
                      fontSize="11" fill={NEUTRAL.faint}>
                  {d.label}
                </text>
              )}
              {/* Invisible hover overlay covering the full column */}
              <rect x={xCenter - colW / 2} y={pad.t}
                    width={colW} height={ch}
                    fill="transparent"
                    onMouseMove={(e) => show(e, tooltipFor(i))}
                    onMouseLeave={hide}/>
            </g>
          );
        })}
        {/* Net line: monotone-cubic (rule 6), dark, dots on the real
            points (extremes emphasized). Drawn above the bars. */}
        {netLine && (
          <g pointerEvents="none">
            <path {...netDraw} d={monotonePath(netPts)} fill="none" stroke={netColor}
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
              {netPts.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r={lineDotRadius(n, i === 0 || i === n - 1)}
                        fill={netColor}/>
              ))}
            </EntranceGroup>
          </g>
        )}
        {/* Legend: aggregated entries only, centered single row */}
        {legendItems.map((item) => {
          const gx = legendX;
          legendX += item.w;
          return (
            <g key={item.id} transform={`translate(${gx},${h - LEGEND_H + 4})`}>
              {item.shape === 'line' ? (
                <>
                  <line x1="0" y1="5" x2="12" y2="5" stroke={item.color} strokeWidth="2.5" strokeLinecap="round"/>
                  <circle cx="6" cy="5" r="2.2" fill={item.color} stroke="white" strokeWidth="1"/>
                </>
              ) : (
                <rect width="9" height="9" rx="2.5" fill={item.color} y="0.5"/>
              )}
              <text x="15" y="8.5" fontSize="11" fill={NEUTRAL.muted}>{item.label}</text>
            </g>
          );
        })}
      </svg>
    </ChartSVGWrap>
  );
}


// ── Grouped Bar ──────────────────────────────────────────────
// Rule 1: per-bar labels (at thinned group indices, when bars are
// wide enough to carry them). Rule 3: legend-table with last value +
// delta chip per key.
export function GroupedBar({ data, keys, colors, height = 200, width: widthHint = 400, dataLabels, showValues = true, axisYFormat, entrance = true, ariaLabel = 'Barras agrupadas' }) {
  const componentTheme = useColorTheme('GroupedBar');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) colors = componentTheme.palette;
  if (colors === undefined) colors = clientColors;

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const allVals = data.flatMap(d => keys.map(k => d[k] || 0));
  // Round axis ticks; the scale max IS the top tick (≥ data max).
  const { ticks: yTicks, max } = niceTicks(safeMax(allVals));
  const n = data.length, gk = keys.length;
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  const labelFmt = resolveLabels(dataLabels, showValues);
  const totals = data.map(d => keys.reduce((s, k) => s + (d[k] || 0), 0));
  const groupLabelIdx = labelFmt ? thinLabelIndices(totals) : null;
  const legendItems = keys.map((k, ki) => {
    const { value, delta } = lastDelta(data.map(d => d[k] || 0));
    return { name: k, color: colors[ki % colors.length], value: value != null ? yAxisFmt(value) : null, delta };
  });
  const lgd = legendLayout(legendItems, Math.max(120, w - 16));
  const h = height, pad = { l: 48, r: 12, t: 14, b: 30 + lgd.height };
  const cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  const gap = 2, maxBw = 22;
  const groupW = Math.min(cw / n * 0.65, maxBw * gk + gap * (gk - 1));
  const bw = (groupW - gap * (gk - 1)) / gk;
  const rx = Math.min(6, Math.max(2, bw / 2));
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.l} x2={w - pad.r} y1={pad.t + ch * (1 - v / max)} y2={pad.t + ch * (1 - v / max)} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l - 6} y={pad.t + ch * (1 - v / max) + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text>
          </g>
        ))}
        {data.map((d, i) => {
          const gx = pad.l + (i + 0.5) * (cw / n) - groupW / 2;
          return (
            <g key={i}>
              {keys.map((k, ki) => { const val = d[k] || 0, bh = (val / max) * ch; return (
                <g key={k}>
                  <EntranceGroup mode="growY" origin={`0px ${pad.t + ch}px`} disabled={!entrance} drawn={chartDrawn}>
                    <rect x={gx + ki * (bw + gap)} y={pad.t + ch - bh} width={bw} height={bh} fill={colors[ki % colors.length]} rx={rx} opacity="0.85"/>
                  </EntranceGroup>
                  {/* Rule 1: labels when bars are wide enough, thinned by group */}
                  {labelFmt && bw >= 16 && groupLabelIdx.has(i) && <text x={gx + ki * (bw + gap) + bw/2} y={pad.t + ch - bh - 3} textAnchor="middle" fontSize="11" fontWeight="700" fill={NEUTRAL.text}>{labelFmt(val)}</text>}
                </g>
              ); })}
              <SlotLabel label={d.label} slot={cw / n} x={gx + groupW / 2} y={pad.t + ch + 16} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint} />
            </g>
          );
        })}
        {/* Legend-table (rule 3) */}
        <SVGLegendRows layout={lgd} width={w} top={h - lgd.height} />
      </svg>
    </div>
  );
}

// ── Line Chart ───────────────────────────────────────────────
// Rule 6: monotone-cubic smoothing (zero overshoot; dots mark the
// real points). Rule 2: 2.5px stroke + dots r=2.2 (extremes 3.6).
// Rule 1: labels ON by default, auto-thinned.
export function LineChart({ data, color, height = 200, width: widthHint = 400, showArea = true, showDots = true, dataLabels, showValues = true, axisYFormat, entrance = true, ariaLabel = 'Gráfico de línea' }) {
  const componentTheme = useColorTheme('LineChart');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  // Entrance: the stroke draws in; hooks stay unconditional (top).
  const lineDraw = useLineDraw(!entrance, lineDataSig(data));
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const vals = data.map(d => d.value);
  // Round axis ticks; domain snaps to the nice floor/ceil of the data.
  const { ticks: yTicks, max, min } = niceTicks(safeMax(vals), 4, safeMin(vals));
  const h = height, pad = { l: 48, r: 12, t: 14, b: 30 }, cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  const _range = (max - min) || 1;
  const n = data.length;
  const pts = data.map((d, i) => ({ x: lineX(i, n, pad.l, cw), y: pad.t + (1 - (d.value - min) / _range) * ch }));
  const lineD = monotonePath(pts);
  const areaD = `${lineD}L${pts[n - 1].x.toFixed(2)},${(pad.t + ch).toFixed(2)}L${pts[0].x.toFixed(2)},${(pad.t + ch).toFixed(2)}Z`;
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  const labelFmt = resolveLabels(dataLabels, showValues);
  const labelIdx = labelFmt ? thinLabelIndices(vals) : null;
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {yTicks.map(v => { const y = pad.t + (1 - (v - min) / _range) * ch; return (
          <g key={v}>
            <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l - 6} y={y + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text>
          </g>
        ); })}
        {showArea && (
          <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
            <path d={areaD} fill={color} opacity="0.08"/>
          </EntranceGroup>
        )}
        <path {...lineDraw} d={lineD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
          {showDots && pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={lineDotRadius(n, i === 0 || i === n - 1)} fill={color}/>)}
          {labelFmt && pts.map((p, i) => labelIdx.has(i) && (
            <text key={`lbl${i}`} x={p.x} y={Math.max(pad.t + 10, p.y - 9)} textAnchor="middle" fontSize="11" fontWeight="700" fill={color}>{labelFmt(data[i].value)}</text>
          ))}
        </EntranceGroup>
        {data.map((d, i) => i % xLabelStride(data.map(x => x.label), cw, 8) === 0 && (
          <text key={i} x={pts[i].x} y={h - pad.b + 16} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint}>{d.label}</text>
        ))}
      </svg>
    </div>
  );
}

// ── Multi-Line ───────────────────────────────────────────────
// Rule 6: monotone curves. Rule 2: 2.5px strokes + dots. Rule 1:
// labels ON default (extra thinning with 3+ series). Rule 3:
// legend-table with last value + delta chip per series.
export function MultiLine({ series, labels, colors, height = 200, width: widthHint = 400, dataLabels, showValues = true, axisYFormat, entrance = true, ariaLabel = 'Gráfico multi-serie' }) {
  const componentTheme = useColorTheme('MultiLine');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) colors = componentTheme.palette;
  if (colors === undefined) colors = clientColors;

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(series), [series]), !entrance);
  // Entrance: one shared draw-in spread on every solid series stroke.
  const lineDraw = useLineDraw(!entrance, lineDataSig(series));
  if (!hasData(series) || !hasData(labels)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const allVals = series.flatMap(s => s.data);
  // Round axis ticks; domain snaps to the nice floor/ceil of the data.
  const { ticks: yTicks, max, min } = niceTicks(safeMax(allVals), 4, safeMin(allVals));
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  const labelFmt = resolveLabels(dataLabels, showValues);
  const labelIdxSets = labelFmt
    ? series.map(s => (series.length < 3 ? thinLabelIndices(s.data) : peakLabelIndices(s.data)))
    : null;
  const legendItems = series.map((s, si) => {
    const { value, delta } = lastDelta(s.data);
    return { name: s.name, color: colors[si % colors.length], shape: 'line', value: value != null ? yAxisFmt(value) : null, delta };
  });
  const lgd = legendLayout(legendItems, Math.max(120, w - 16));
  const h = height, pad = { l: 48, r: 12, t: 14, b: 26 + lgd.height }, cw = w - pad.l - pad.r, ch = h - pad.t - pad.b, n = labels.length;
  const _r = (max - min) || 1;
  const px = i => lineX(i, n, pad.l, cw);
  const py = v => pad.t + (1 - (v - min) / _r) * ch;
  // Anti-collision: labels sharing an x slot are separated ≥ 12px
  // vertically (higher value stays up), clamped to the plot area.
  const seriesLabels = labelFmt ? (() => {
    const byIdx = new Map();
    series.forEach((s, si) => {
      s.data.forEach((v, i) => {
        if (!labelIdxSets[si].has(i)) return;
        if (typeof v !== 'number' || !Number.isFinite(v)) return;
        if (!byIdx.has(i)) byIdx.set(i, []);
        byIdx.get(i).push({ si, i, v, y: py(v) - 9 });
      });
    });
    const out = [];
    for (const group of byIdx.values()) {
      const ys = separateLabelYs(group.map(g => g.y), 12, pad.t + 10, pad.t + ch - 2);
      group.forEach((g, k) => out.push({ ...g, y: ys[k] }));
    }
    return out;
  })() : null;
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {yTicks.map(v => { const y = pad.t + (1 - (v - min) / _r) * ch; return (
          <g key={v}>
            <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l - 6} y={y + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text>
          </g>
        ); })}
        {series.map((s, si) => {
          const pts = s.data.map((v, i) => ({ x: px(i), y: py(v) }));
          return <path key={si} {...lineDraw} d={monotonePath(pts)} fill="none" stroke={colors[si % colors.length]} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9"/>;
        })}
        {/* Rule 2: solid density-adaptive dots (extremes emphasized) */}
        <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
          {series.map((s, si) => s.data.map((v, i) => {
            if (typeof v !== 'number' || !Number.isFinite(v)) return null;
            return <circle key={`dot${si}-${i}`} cx={px(i)} cy={py(v)} r={lineDotRadius(n, i === 0 || i === n - 1)} fill={colors[si % colors.length]}/>;
          }))}
          {seriesLabels && seriesLabels.map(g => (
            <text key={`lbl${g.si}-${g.i}`} x={px(g.i)} y={g.y} textAnchor="middle" fontSize="11" fontWeight="700" fill={colors[g.si % colors.length]}>{labelFmt(g.v)}</text>
          ))}
        </EntranceGroup>
        {labels.map((l, i) => i % xLabelStride(labels, cw, 6) === 0 && (
          <text key={i} x={px(i)} y={pad.t + ch + 16} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint}>{l}</text>
        ))}
        {/* Legend-table (rule 3) */}
        <SVGLegendRows layout={lgd} width={w} top={h - lgd.height} />
      </svg>
    </div>
  );
}

// ── Stacked Area ─────────────────────────────────────────────
// Rule 6: layer boundaries are monotone-cubic curves (both edges of
// each band, zero overshoot). Rule 1: stack-total labels (thinned).
// Rule 3: legend-table (the chart had no legend at all before).
export function StackedArea({ series, labels, colors, height = 200, width: widthHint = 400, dataLabels, showValues = true, axisYFormat, entrance = true, ariaLabel = 'Área apilada' }) {
  const componentTheme = useColorTheme('StackedArea');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) colors = componentTheme.palette;
  if (colors === undefined) colors = clientColors;

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(series), [series]), !entrance);
  if (!hasData(series) || !hasData(labels)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const n = labels.length, stacked = [];
  for (let i = 0; i < n; i++) { let cum = 0; stacked[i] = series.map(s => { cum += s.data[i]; return cum; }); }
  // Round axis ticks; the scale max IS the top tick (≥ data max).
  const { ticks: yTicks, max } = niceTicks(safeMax(stacked.map(s => s[s.length - 1])));
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  const labelFmt = resolveLabels(dataLabels, showValues);
  const totals = Array.from({ length: n }, (_, i) => stacked[i][series.length - 1]);
  const totalLabelIdx = labelFmt ? thinLabelIndices(totals) : null;
  const legendItems = series.map((s, si) => {
    const { value, delta } = lastDelta(s.data);
    return { name: s.name, color: colors[si % colors.length], value: value != null ? yAxisFmt(value) : null, delta };
  });
  const lgd = legendLayout(legendItems, Math.max(120, w - 16));
  const h = height, pad = { l: 48, r: 12, t: 14, b: 26 + lgd.height }, cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  const px = i => lineX(i, n, pad.l, cw), py = v => pad.t + (1 - v / max) * ch;
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.l} x2={w - pad.r} y1={py(v)} y2={py(v)} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l - 6} y={py(v) + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text>
          </g>
        ))}
        <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
        {[...series].reverse().map((_s, si) => {
          const ri = series.length - 1 - si;
          const topPts = Array.from({ length: n }, (_, i) => ({ x: px(i), y: py(stacked[i][ri]) }));
          const botPts = ri === 0
            ? Array.from({ length: n }, (_, i) => ({ x: px(n - 1 - i), y: py(0) }))
            : Array.from({ length: n }, (_, i) => ({ x: px(n - 1 - i), y: py(stacked[n - 1 - i][ri - 1]) }));
          const d = `${monotonePath(topPts)}${monotonePath(botPts).replace(/^M/, 'L')}Z`;
          return <path key={ri} d={d} fill={colors[ri % colors.length]} opacity="0.82" stroke="white" strokeWidth="0.6"/>;
        })}
        </EntranceGroup>
        {labelFmt && totals.map((total, i) => (total > 0 && totalLabelIdx.has(i) ? (
          <text key={`lbl${i}`} x={px(i)} y={Math.max(pad.t + 10, py(total) - 6)} textAnchor="middle" fontSize="11" fontWeight="700" fill={NEUTRAL.text}>{labelFmt(total)}</text>
        ) : null))}
        {labels.map((l, i) => i % xLabelStride(labels, cw, 6) === 0 && (
          <text key={i} x={px(i)} y={pad.t + ch + 16} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint}>{l}</text>
        ))}
        {/* Legend-table (rule 3) */}
        <SVGLegendRows layout={lgd} width={w} top={h - lgd.height} />
      </svg>
    </div>
  );
}

// ── Pie / Donut ──────────────────────────────────────────────
// Redesign: donut with a central total metric is the default render
// (`donut={false}` still gives the classic pie) — mirror of
// `PieChartT` minus hover. Segment % labels paint inside slices
// ≥ 10% (rule 1) and the legend is a legend-table: swatch + name +
// value (bold) + share (rule 3).
export function PieChart({ data, colors, height = 200, donut = true, width: widthHint = 360, showValues = true, entrance = true, ariaLabel }) {
  const componentTheme = useColorTheme('PieChart');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) colors = componentTheme.palette;
  if (colors === undefined) colors = clientColors;

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const total = data.reduce((s, d) => s + d.value, 0);
  const r = Math.min(w * 0.22, height * 0.38);
  const fs = Math.max(9, Math.min(12, r * 0.145));
  // Pie + legend centred as one block (pieCenterX, shared.jsx).
  const cx = pieCenterX(w, r, fs, data, total), cy = height / 2;
  const ir = donut ? r * 0.585 : 0;
  let angle = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const sweep = (total > 0 ? (d.value / total) : 0) * Math.PI * 2;
    const a0 = angle;
    const x1o = cx + r * Math.cos(angle), y1o = cy + r * Math.sin(angle);
    const x1i = cx + ir * Math.cos(angle), y1i = cy + ir * Math.sin(angle);
    angle += sweep;
    const x2o = cx + r * Math.cos(angle), y2o = cy + r * Math.sin(angle);
    const x2i = cx + ir * Math.cos(angle), y2i = cy + ir * Math.sin(angle);
    const large = sweep > Math.PI ? 1 : 0;
    const path = donut
      ? `M${x1i},${y1i} L${x1o},${y1o} A${r},${r} 0 ${large} 1 ${x2o},${y2o} L${x2i},${y2i} A${ir},${ir} 0 ${large} 0 ${x1i},${y1i}`
      : `M${cx},${cy} L${x1o},${y1o} A${r},${r} 0 ${large} 1 ${x2o},${y2o} Z`;
    const mid = a0 + sweep / 2;
    const labelR = donut ? (r + ir) / 2 : r * 0.62;
    return {
      ...d, path, color: colors[i % colors.length],
      pct: total > 0 ? ((d.value / total) * 100).toFixed(1) : '0.0',
      pctNum: total > 0 ? (d.value / total) * 100 : 0,
      lx: cx + labelR * Math.cos(mid), ly: cy + labelR * Math.sin(mid),
    };
  });
  const legX = cx + r + 22, legRowH = Math.max(24, (height - 40) / data.length);
  const legStartY = cy - (data.length - 1) * legRowH / 2;
  return (
    <div ref={wrapRef} style={{ height }} className="w-full overflow-hidden">
      <svg width={w} height={height} role="img" aria-label={ariaLabel || (donut ? 'Donut' : 'Torta')}>
        <EntranceGroup mode="pop" origin={`${cx}px ${cy}px`} disabled={!entrance} drawn={chartDrawn}>
          {slices.map((s, i) => <path key={i} d={s.path} fill={s.color} opacity="0.9" className="hover:opacity-100 transition-opacity cursor-pointer"/>)}
        </EntranceGroup>
        {/* Rule 1: % inside segments when the slice is ≥ 10% */}
        {showValues !== false && slices.map((s, i) => s.pctNum >= 10 && (
          <text key={`pct${i}`} x={s.lx} y={s.ly + 3.5} textAnchor="middle" fontSize={Math.max(11, fs - 1)}
            fontWeight="800" fill="white" pointerEvents="none">{Math.round(s.pctNum)}%</text>
        ))}
        {donut && <>
          <text x={cx} y={cy - 2} textAnchor="middle" fontSize={r * 0.27} fontWeight="800" fill={NEUTRAL.text}>{fmtN(total)}</text>
          <text x={cx} y={cy + r * 0.18} textAnchor="middle" fontSize={Math.max(11, r * 0.13)} fill={NEUTRAL.faint}>Total</text>
        </>}
        {slices.map((s, i) => (
          <g key={i} transform={`translate(${legX}, ${legStartY + i * legRowH - 5})`}>
            <rect width="10" height="10" rx="2.5" fill={s.color} y="1"/>
            <text x="16" y="10" fontSize={fs} fill={NEUTRAL.text} fontWeight="500">{s.label}</text>
            <text x="16" y={10 + fs * 1.2} fontSize={fs - 1} fill={NEUTRAL.muted}>
              <tspan fontWeight="700" fill={NEUTRAL.text}>{fmtN(s.value)}</tspan>
              <tspan> · {s.pct}%</tspan>
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

// ── Scatter ──────────────────────────────────────────────────
// AXIS_INSET reserves a buffer between the visual axis and the
// origin (x=0, y=0) so data marks at the edge of the domain don't
// sit on top of (or get clipped by) the axis labels. Without this,
// a point at (0, 0) renders with its centre on the axis cross —
// visually noisy when point size is non-trivial.
export function ScatterChart({ data, color, height = 200, width: widthHint = 400, dataLabels, showValues = true, entrance = true, ariaLabel = 'Dispersión' }) {
  const componentTheme = useColorTheme('ScatterChart');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  // Round ticks on both axes; the domain max IS the top tick.
  const { ticks: xTicks, max: xMax } = niceTicks(safeMax(data.map(d => d.x)));
  const { ticks: yTicks, max: yMax } = niceTicks(safeMax(data.map(d => d.y)));
  const h = height, pad = { l: 44, r: 16, t: 14, b: 30 }, cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  const AXIS_INSET_X = 12, AXIS_INSET_Y = 12;
  const dataW = Math.max(0, cw - AXIS_INSET_X), dataH = Math.max(0, ch - AXIS_INSET_Y);
  const xToCx = (x) => pad.l + AXIS_INSET_X + (x / xMax) * dataW;
  const yToCy = (y) => pad.t + (1 - y / yMax) * dataH;
  // Rule 1 with scatter-specific thinning: points are unordered, so
  // default labels only render for small clouds (n ≤ 12); an explicit
  // editor spec (`dataLabels.show: true`) labels every point.
  const _lblFmt = resolveLabels(dataLabels, showValues);
  const labelFmt = _lblFmt && (dataLabels?.show === true || data.length <= 12) ? _lblFmt : null;
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.l} x2={w - pad.r} y1={yToCy(v)} y2={yToCy(v)} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l - 6} y={yToCy(v) + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{v.toFixed(0)}</text>
          </g>
        ))}
        {xTicks.map(v => <text key={v} x={xToCx(v)} y={h - pad.b + 16} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint}>{v.toFixed(0)}</text>)}
        <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
          {data.map((d, i) => (
            <g key={i}>
              <circle cx={xToCx(d.x)} cy={yToCy(d.y)} r={d.size || 4} fill={d.color || color} opacity="0.6"/>
              {labelFmt && <text x={xToCx(d.x)} y={yToCy(d.y) - (d.size || 4) - 4} textAnchor="middle" fontSize="11" fontWeight="700" fill={NEUTRAL.text}>{labelFmt(d.y)}</text>}
            </g>
          ))}
        </EntranceGroup>
      </svg>
    </div>
  );
}

// ── Bubble ───────────────────────────────────────────────────
// AXIS_INSET sized to the largest possible bubble radius so even a
// point at (0, 0) sits fully inside the plot area instead of
// straddling the X / Y axis labels. See ScatterChart for the same
// pattern with a smaller inset.
export function BubbleChart({ data, colors, height = 220, width: widthHint = 400, dataLabels, showValues = true, entrance = true, ariaLabel = 'Burbuja' }) {
  const componentTheme = useColorTheme('BubbleChart');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) colors = componentTheme.palette;
  if (colors === undefined) colors = clientColors;

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  // Round ticks on both axes; the domain max IS the top tick.
  const { ticks: xTicks, max: xMax } = niceTicks(safeMax(data.map(d => d.x)));
  const { ticks: yTicks, max: yMax } = niceTicks(safeMax(data.map(d => d.y)));
  const sMax = safeMax(data.map(d => d.size));
  const h = height, pad = { l: 48, r: 16, t: 14, b: 30 }, cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  const MAX_R = 23; // 5 base + 18 size factor
  const AXIS_INSET_X = MAX_R + 6, AXIS_INSET_Y = MAX_R + 6;
  const dataW = Math.max(0, cw - AXIS_INSET_X), dataH = Math.max(0, ch - AXIS_INSET_Y);
  const xToCx = (x) => pad.l + AXIS_INSET_X + (x / xMax) * dataW;
  const yToCy = (y) => pad.t + (1 - y / yMax) * dataH;
  // Rule 1: same small-cloud criterion as scatter (n ≤ 12 by default).
  const _lblFmt = resolveLabels(dataLabels, showValues);
  const labelFmt = _lblFmt && (dataLabels?.show === true || data.length <= 12) ? _lblFmt : null;
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.l} x2={w - pad.r} y1={yToCy(v)} y2={yToCy(v)} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l - 6} y={yToCy(v) + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{fmtN(v)}</text>
          </g>
        ))}
        {xTicks.map(v => (
          <text key={v} x={xToCx(v)} y={h - pad.b + 16} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint}>{fmtN(v)}</text>
        ))}
        <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
          {data.map((d, i) => {
            const r = 5 + (d.size / sMax) * 18;
            return (
              <g key={i}>
                <circle cx={xToCx(d.x)} cy={yToCy(d.y)} r={r} fill={colors[i % colors.length]} opacity="0.55" stroke={colors[i % colors.length]} strokeWidth="1"/>
                {labelFmt && <text x={xToCx(d.x)} y={yToCy(d.y) - r - 4} textAnchor="middle" fontSize="11" fontWeight="700" fill={NEUTRAL.text}>{labelFmt(d.size ?? d.y)}</text>}
              </g>
            );
          })}
        </EntranceGroup>
      </svg>
    </div>
  );
}

// ── Gauge ────────────────────────────────────────────────────
export function Gauge({ value, max = 100, label, color, height = 180, width: widthHint = 240, entrance = true, ariaLabel = 'Velocímetro' }) {
  const componentTheme = useColorTheme('Gauge');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Entrance: the value arc sweeps in via normalized dash offset.
  const arcDraw = useLineDraw(!entrance, lineDataSig(value));
  const p = value / max, cx = w / 2, cy = height * 0.6, r = Math.min(w * 0.33, height * 0.44);
  const sa = Math.PI * 0.75, ea = Math.PI * 2.25;
  const arc = (a1, a2) => { const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1), x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2), lg = (a2 - a1) > Math.PI ? 1 : 0; return `M${x1},${y1} A${r},${r} 0 ${lg} 1 ${x2},${y2}`; };
  const fs = Math.max(10, r * 0.38);
  return (
    <div ref={wrapRef} style={{ height }} className="w-full overflow-hidden">
      <svg width={w} height={height} role="img" aria-label={`${ariaLabel}: ${value} de ${max}`}>
        <path d={arc(sa, ea)} fill="none" stroke={NEUTRAL.track} strokeWidth="14" strokeLinecap="round"/>
        <path {...arcDraw} d={arc(sa, sa + p * (ea - sa))} fill="none" stroke={color} strokeWidth="14" strokeLinecap="round"/>
        <text x={cx} y={cy} textAnchor="middle" fontSize={fs} fontWeight="800" fill={NEUTRAL.text}>{Math.round(value)}</text>
        <text x={cx} y={cy + fs * 0.75} textAnchor="middle" fontSize={Math.max(11, fs * 0.38)} fill={NEUTRAL.faint}>{label || `of ${max}`}</text>
      </svg>
    </div>
  );
}

// ── Radar ────────────────────────────────────────────────────
export function RadarChart({ data, color, height = 220, width: widthHint = 400, dataLabels, showValues = true, entrance = true, ariaLabel = 'Radar' }) {
  const componentTheme = useColorTheme('RadarChart');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const maxR = Math.min(w / 2, height / 2);
  const fs = Math.max(11, Math.min(14, maxR * 0.68 * 0.13));
  const labelFmt = resolveLabels(dataLabels, showValues);
  // Room for the side labels: the radius gives way to the longest
  // "axis value" text (0.6 em per character) so it never runs off the
  // card; the top / bottom labels keep a line of room too.
  const labelW = Math.max(0, ...data.map(d => (String(d.axis ?? d.label ?? '').length + (labelFmt ? String(labelFmt(d.value)).length + 1 : 0)) * fs * 0.6));
  const r = Math.max(24, Math.min(maxR * 0.68, w / 2 - labelW - 8, height / 2 - fs * 1.8)), cx = w / 2, cy = height / 2;
  const n = data.length, step = (Math.PI * 2) / n;
  const maxVal = safeMax(data.map(d => d.value));
  const pt = (i, v) => ({ x: cx + (v / maxVal) * r * Math.cos(step * i - Math.PI / 2), y: cy + (v / maxVal) * r * Math.sin(step * i - Math.PI / 2) });
  const poly = data.map((d, i) => { const p = pt(i, d.value); return `${p.x},${p.y}`; }).join(' ');
  // Rule 1: value labels ON by default. The value rides on the axis label
  // ("Compliance 76"), outside the polygon: placed at the vertex it hit
  // the axis label whenever the value was near the max (QA F19,
  // 2026-10-26). Each label anchors on its side so it never runs off the
  // card.
  const anchorAt = (i) => {
    const c = Math.cos(step * i - Math.PI / 2);
    return c > 0.3 ? 'start' : c < -0.3 ? 'end' : 'middle';
  };
  return (
    <div ref={wrapRef} style={{ height }} className="w-full overflow-hidden">
      <svg width={w} height={height} role="img" aria-label={ariaLabel}>
        {[.33, .66, 1].map(f => (
          <polygon key={f} points={data.map((_, i) => { const p = pt(i, maxVal * f); return `${p.x},${p.y}`; }).join(' ')} fill="none" stroke={NEUTRAL.border} strokeWidth="0.6"/>
        ))}
        {data.map((_, i) => { const p = pt(i, maxVal); return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={NEUTRAL.border} strokeWidth="0.5"/>; })}
        <EntranceGroup mode="pop" origin={`${cx}px ${cy}px`} disabled={!entrance} drawn={chartDrawn}>
          <polygon points={poly} fill={color} opacity="0.15" stroke={color} strokeWidth="2.5"/>
        </EntranceGroup>
        {data.map((d, i) => { const p = pt(i, d.value), lp = pt(i, maxVal * 1.18); return (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="3.5" fill="white" stroke={color} strokeWidth="2"/>
            <text x={lp.x} y={lp.y + 4} textAnchor={anchorAt(i)} fontSize={fs} fill={NEUTRAL.muted} fontWeight="500">
              {d.axis ?? d.label}
              {labelFmt && <tspan dx="4" fill={NEUTRAL.text} fontWeight="700">{labelFmt(d.value)}</tspan>}
            </text>
          </g>
        ); })}
      </svg>
    </div>
  );
}

// ── Waterfall ────────────────────────────────────────────────
// Rule 1: step labels ON by default (thinned when dense). Semantic
// colors come from the brand tokens (green positive / red ONLY
// negative); totals stay on the primary series color.
export function WaterfallChart({ data, height = 220, width: widthHint = 400, dataLabels, showValues = true, axisYFormat, entrance = true, ariaLabel = 'Cascada' }) {
  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const h = height, pad = { l: 48, r: 12, t: 16, b: 38 }, cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  // A `type: 'total'` row (contract: index.d.ts WaterfallChart; GenericChart
  // maps the SQL `type` column) is an absolute LEVEL drawn from 0 that
  // resets the running sum — not one more step (audit #208).
  let running = 0;
  const items = data.map(d => {
    const isTotal = d.type === 'total' || !!d.total;
    if (isTotal) { running = d.value; return { ...d, isTotal, start: 0, end: d.value }; }
    const start = running; running += d.value; return { ...d, isTotal, start, end: running };
  });
  const allVals = items.flatMap(d => [d.start, d.end]);
  // Round axis ticks; the domain snaps to the nice floor (≤ min,
  // possibly negative) and ceil (≥ max) of the running totals.
  const { ticks: yTicks, max, min } = niceTicks(
    safeMax(allVals), 4, Math.min(0, safeMin(allVals)),
  );
  const range = max - min || 1;
  const py = v => pad.t + (1 - (v - min) / range) * ch, n = items.length, bw = Math.min(cw / n * 0.6, 32);
  const rx = Math.min(6, bw / 2);
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  const labelFmt = resolveLabels(dataLabels, showValues);
  const labelIdx = labelFmt ? thinLabelIndices(items.map(d => d.value)) : null;
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.l} x2={w - pad.r} y1={py(v)} y2={py(v)} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l - 6} y={py(v) + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text>
          </g>
        ))}
        {items.map((d, i) => {
          const x = pad.l + (i + 0.5) * (cw / n) - bw / 2, y1 = py(d.start), y2 = py(d.end), top = Math.min(y1, y2), bh = Math.abs(y2 - y1);
          const clr = d.isTotal ? PALETTE[0] : d.value >= 0 ? SEMANTIC.bandPositive : SEMANTIC.bandNegative;
          return (
            <g key={i}>
              <EntranceGroup mode="growY" origin={`0px ${py(0)}px`} disabled={!entrance} drawn={chartDrawn}>
                <rect x={x} y={top} width={bw} height={Math.max(bh, 1)} fill={clr} rx={rx} opacity="0.85"/>
              </EntranceGroup>
              {labelFmt && labelIdx.has(i) && <text x={x + bw / 2} y={Math.max(pad.t + 8, top - 4)} textAnchor="middle" fontSize="11" fontWeight="700" fill={clr}>{labelFmt(d.value)}</text>}
              {i < items.length - 1 && <line x1={x + bw} y1={py(d.end)} x2={x + bw + (cw / n) * 0.4} y2={py(d.end)} stroke={NEUTRAL.axis} strokeWidth="0.8" strokeDasharray="2,2"/>}
              <text x={x + bw / 2} y={h - pad.b + 16} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint}>{d.label}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Funnel ───────────────────────────────────────────────────
export function FunnelChart({ data, colors, height = 220, width: widthHint = 400, dataLabels, showValues = true, entrance = true, ariaLabel = 'Embudo' }) {
  const componentTheme = useColorTheme('FunnelChart');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) colors = componentTheme.palette;
  if (colors === undefined) colors = clientColors;

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const max = data[0]?.value || 1, h = height, pad = 22, ch = h - pad * 2, n = data.length, stepH = ch / n;
  const labelFmt = resolveLabels(dataLabels, showValues, v => v.toLocaleString());
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {data.map((d, i) => {
          const wPct = d.value / max, nextWPct = data[i + 1] ? data[i + 1].value / max : wPct * 0.8;
          const cx = w / 2, topW = wPct * (w - 100), botW = nextWPct * (w - 100), y = pad + i * stepH;
          const path = `M${cx - topW / 2},${y} L${cx + topW / 2},${y} L${cx + botW / 2},${y + stepH} L${cx - botW / 2},${y + stepH} Z`;
          return (
            <g key={i}>
              <EntranceGroup mode="pop" origin={`${w / 2}px ${h / 2}px`} disabled={!entrance} drawn={chartDrawn}>
                <path d={path} fill={colors[i % colors.length]} opacity="0.85"/>
              </EntranceGroup>
              <text x={cx} y={y + stepH / 2 + 2} textAnchor="middle" fontSize="11" fontWeight="600" fill="white">{d.label}</text>
              {labelFmt && <text x={cx} y={y + stepH / 2 + 14} textAnchor="middle" fontSize="11" fontWeight="700" fill="rgba(255,255,255,0.85)">{labelFmt(d.value)}</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Treemap ──────────────────────────────────────────────────
export function TreemapChart({ data, colors, height = 220, width: widthHint = 400, dataLabels, showValues = true, entrance = true, ariaLabel = 'Treemap' }) {
  const componentTheme = useColorTheme('TreemapChart');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) colors = componentTheme.palette;
  if (colors === undefined) colors = clientColors;

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const total = data.reduce((s, d) => s + d.value, 0), cw = w - 20, ch = height - 20;
  // All-zero (or non-numeric) total: 0/0 → NaN geometry and a blank card
  // (audit #215). Show the empty state instead.
  if (!(total > 0)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const sorted = [...data].sort((a, b) => b.value - a.value);
  let rects = [], y = 0;
  sorted.forEach((d, i) => { const rh = (d.value / total) * ch; rects.push({ ...d, x: 10, y, w: cw, h: rh, color: colors[i % colors.length] }); y += rh; });
  const labelFmt = resolveLabels(dataLabels, showValues);
  return (
    <div ref={wrapRef} style={{ height }} className="w-full overflow-hidden">
      <svg width={w} height={height} role="img" aria-label={ariaLabel}>
        {rects.map((r, i) => {
          const cy = r.y + 5 + r.h / 2 + 3;
          const share = total > 0 ? (r.value / total) * 100 : 0;
          return (
            <g key={i}>
              <EntranceGroup mode="pop" origin={`${w / 2}px ${height / 2}px`} disabled={!entrance} drawn={chartDrawn}>
                <rect x={r.x} y={r.y + 5} width={r.w} height={Math.max(r.h - 2, 1)} fill={r.color} rx="6" opacity="0.9"/>
              </EntranceGroup>
              {/* Rule 1: label left + bold value + share right, single inline row */}
              {r.h > 15 && <text x={r.x + 12} y={cy} fontSize="11" fontWeight="600" fill="white">{r.label}</text>}
              {labelFmt && r.h > 15 && (
                <text x={r.x + r.w - 12} y={cy} textAnchor="end" fontSize="11" fill="white">
                  <tspan fontWeight="800">{labelFmt(r.value)}</tspan>
                  <tspan fontWeight="600" opacity="0.75">{`  ·  ${share.toFixed(1)}%`}</tspan>
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Heatmap Chart ────────────────────────────────────────────
// F3b: cells ramp on the shared HEAT_RAMP (magenta, accent 50→900)
// instead of the old inline teal ladder.
// ── HeatmapChart axis labels ─────────────────────────────────
// Approximate glyph widths for the axis labels, both at 11px since the
// type floor of QA look v3 F16 (they were 9.5px / 8.5px; with the old
// widths "BodegaAurrera" started 4px left of the SVG in stg).
const HEAT_Y_CHAR_PX = 6.5;
const HEAT_X_CHAR_PX = 6.5;
const HEAT_Y_MIN_PX = 40;
const HEAT_Y_MAX_PX = 136;
const HEAT_Y_MAX_CHARS = 20;
const HEAT_X_MAX_CHARS = 18;

/** Truncate axis labels to `maxChars` (end ellipsis; full text kept for
 *  a <title>) and report the longest resulting label. */
export function heatAxisLabels(labels, maxChars) {
  const rows = labels.map((raw) => {
    const full = String(raw ?? '');
    const truncated = full.length > maxChars;
    const text = truncated ? `${full.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…` : full;
    return { text, full, truncated };
  });
  const longest = rows.reduce((m, r) => Math.max(m, r.text.length), 0);
  return { rows, longest };
}

// `onCellClick(xi, yi)` makes the cells that HAVE a reading clickable
// (pointer, Enter/Space, `clickHint` in the cell's <title>); absent
// cells stay inert — there is nothing behind them to open.
export function HeatmapChart({ data, xLabels, yLabels, height = 220, width: widthHint = 400, showValues = true, entrance = true, ariaLabel = 'Heatmap', onCellClick, clickHint, format, colorScale }) {
  // Optional value formatter (percent, currency...). Absent = the raw value, as before.
  const show = (v) => (format ? format(v) : v);
  const theme = useColorTheme('HeatmapChart');
  const colors = theme.override ? theme.heatScale : (colorScale?.length ? colorScale : theme.heatScale);
  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  // Gaps are excluded from the DOMAIN too: a missing cell counted as 0
  // would stretch the ramp and re-colour every real cell around it.
  const allVals = data.flat().filter(v => typeof v === 'number' && Number.isFinite(v));
  const max = safeMax(allVals), min = safeMin(allVals);
  // Axis labels are sized from the DATA, not from a constant. A 58px
  // left pad cut "MercadoLibre" to "ercadoLibre", and 24 subcategory
  // columns at ~45px each stacked their names into one unreadable
  // line (measured on the Disponibilidad report). The y channel grows
  // with the longest row label (capped), and x labels turn -45° as
  // soon as the longest one would not fit its cell; both keep the full
  // text in a native <title>.
  const yText = heatAxisLabels(yLabels, HEAT_Y_MAX_CHARS);
  const xText = heatAxisLabels(xLabels, HEAT_X_MAX_CHARS);
  const yLabelW = Math.min(HEAT_Y_MAX_PX, Math.max(HEAT_Y_MIN_PX, Math.ceil(yText.longest * HEAT_Y_CHAR_PX)));
  const padL = yLabelW + 8, padR = 12, padT = 12;
  const cellW0 = (w - padL - padR) / Math.max(1, xLabels.length);
  const rotateX = xText.longest * HEAT_X_CHAR_PX > cellW0 - 4;
  // Rotated labels project ~0.7× their length onto the vertical axis.
  const padB = rotateX ? Math.ceil(xText.longest * HEAT_X_CHAR_PX * 0.72) + 14 : 32;
  const h = height, pad = { l: padL, r: padR, t: padT, b: padB }, cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  const cellW = cw / xLabels.length, cellH = ch / yLabels.length;
  const heatIdx = v => {
    const t = (v - min) / (max - min || 1);
    return Math.min(colors.length - 1, Math.max(0, Math.floor(t * colors.length)));
  };
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {yText.rows.map((yl, yi) => (
          <text key={yi} x={pad.l - 6} y={pad.t + yi * cellH + cellH / 2 + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.muted}>
            {yl.truncated && <title>{yl.full}</title>}
            {yl.text}
          </text>
        ))}
        {xText.rows.map((xl, xi) => {
          const x = pad.l + xi * cellW + cellW / 2;
          const y = h - pad.b + 12;
          return rotateX ? (
            <text key={xi} x={x} y={y} transform={`rotate(-45 ${x} ${y})`} textAnchor="end" fontSize="11" fill={NEUTRAL.faint} data-axis="x">
              {xl.truncated && <title>{xl.full}</title>}
              {xl.text}
            </text>
          ) : (
            <text key={xi} x={x} y={y + 4} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint} data-axis="x">
              {xl.truncated && <title>{xl.full}</title>}
              {xl.text}
            </text>
          );
        })}
        <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
          {data.map((row, yi) => row.map((val, xi) => {
            // A cell with no row behind it is ABSENT, not zero. Painted
            // as zero it takes one end of the heat ramp, so "we have no
            // reading here" and "the reading is the lowest on the grid"
            // become the same colour — a wrong number told in colour.
            // Absent renders as an outlined empty cell with an em dash:
            // a shape and a glyph, not a hue, because the product owner
            // is red-green colourblind.
            const missing = typeof val !== 'number' || !Number.isFinite(val);
            const idx = missing ? 0 : heatIdx(val);
            const hex = /^#[0-9a-f]{6}$/i.test(colors[idx]) ? colors[idx].slice(1) : 'FFFFFF';
            const rgb = [0,2,4].map(i=>parseInt(hex.slice(i,i+2),16)/255).map(n=>n<=.04045?n/12.92:((n+.055)/1.055)**2.4);
            const darkCell = .2126*rgb[0]+.7152*rgb[1]+.0722*rgb[2] < .179;
            const clickable = !missing && typeof onCellClick === 'function';
            return (
              <g
                key={`${yi}-${xi}`}
                {...(clickable ? {
                  role: 'button', tabIndex: 0, 'data-cell-click': '',
                  'aria-label': `${yLabels[yi]} · ${xLabels[xi]}: ${missing ? '—' : show(val)}`,
                  style: { cursor: 'pointer' },
                  onClick: () => onCellClick(xi, yi),
                  onKeyDown: e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCellClick(xi, yi); } },
                } : {})}
              >
                {clickable && <title>{`${yLabels[yi]} · ${xLabels[xi]}: ${missing ? '—' : show(val)}${clickHint ? `\n${clickHint}` : ''}`}</title>}
                <rect
                  x={pad.l + xi * cellW + 1} y={pad.t + yi * cellH + 1}
                  width={cellW - 2} height={cellH - 2} rx="3"
                  fill={missing ? 'transparent' : colors[idx]}
                  stroke={missing ? NEUTRAL.grid : 'none'}
                  strokeWidth={missing ? 1 : 0}
                  strokeDasharray={missing ? '2 2' : undefined}
                  opacity={1}
                />
                {showValues !== false && (
                  <text
                    x={pad.l + xi * cellW + cellW / 2}
                    y={pad.t + yi * cellH + cellH / 2 + 4}
                    textAnchor="middle" fontSize="11" fontWeight="600"
                    fill={missing ? NEUTRAL.faint : (darkCell ? 'white' : NEUTRAL.text)}
                  >{missing ? '—' : show(val)}</text>
                )}
              </g>
            );
          }))}
        </EntranceGroup>
      </svg>
    </div>
  );
}

// ── Bullet Chart ─────────────────────────────────────────────
// Palette + labels only (no structural redesign): brand semantic
// green when on target / amber when short, neutral chrome, bold
// value label per row (rule 1, `showValues` opt-out).
export function BulletChart({ data, height = 180, width: widthHint = 400, dataLabels, showValues = true, entrance = true, ariaLabel = 'Bullet', missColor, missingTargetMaxLabel = 'Every row needs a numeric target and a max greater than 0' }) {
  const componentTheme = useColorTheme('BulletChart');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) missColor = componentTheme.palette[3 % componentTheme.palette.length];
  if (missColor === undefined) missColor = clientColors[3 % clientColors.length];

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  // Fail loud (owner decision, audit #214): no invented scale or target.
  const invalid = data.some(d => !(Number.isFinite(d.max) && d.max > 0) || !Number.isFinite(d.target));
  if (invalid) return <div ref={wrapRef}><ChartEmpty height={height} message={missingTargetMaxLabel} /></div>;
  const labelFmt = resolveLabels(dataLabels, showValues);
  // Measured label channel (rule 16, like the ranked bars): the fixed
  // 88 px gutter cut "MAP Compliance" and "Listing Coverage" at the left
  // edge (QA F18, 2026-10-26). Labels that still do not fit wrap to two
  // lines, with the full text in <title>.
  const LABEL_GAP = 8;
  const labelMaxPx = Math.max(64, Math.round(w * 0.38) - LABEL_GAP);
  const { rows: labelRows, textW: labelW } = barLabelLayout(data.map(d => d.label), labelMaxPx);
  const h = height, pad = { l: labelW + LABEL_GAP, r: labelFmt ? 54 : 22, t: 6, b: 6 }, cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  const n = data.length, rowH = ch / n;
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {data.map((d, i) => {
          const y = pad.t + i * rowH, max = d.max, barH = rowH * 0.35, trackH = rowH * 0.55;
          const cy = y + rowH / 2;
          return (
            <g key={i}>
              {labelRows[i].wrapped ? (
                <text x={pad.l - LABEL_GAP} y={cy - 2.5} textAnchor="end" fontSize="11" fill={NEUTRAL.muted} fontWeight="500">
                  {labelRows[i].truncated && <title>{labelRows[i].full}</title>}
                  <tspan x={pad.l - LABEL_GAP} dy="0">{labelRows[i].lines[0]}</tspan>
                  <tspan x={pad.l - LABEL_GAP} dy="12">{labelRows[i].lines[1]}</tspan>
                </text>
              ) : (
                <text x={pad.l - LABEL_GAP} y={cy + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.muted} fontWeight="500">{labelRows[i].lines[0]}</text>
              )}
              <rect x={pad.l} y={y + (rowH - trackH) / 2} width={cw} height={trackH} fill={NEUTRAL.track} rx="3"/>
              <rect x={pad.l} y={y + (rowH - trackH) / 2} width={(d.target / max) * cw} height={trackH} fill={NEUTRAL.border} rx="3"/>
              {/* Hit colour is the colour-blind-friendly band green
                  (teal — brand.semantic.bandPositive, 2026-07-21); the
                  miss colour is a prop (default amber, historical) and
                  scorecard dashboards pass the deep band red. */}
              <EntranceGroup mode="growX" origin={`${pad.l}px 0px`} disabled={!entrance} drawn={chartDrawn}>
                <rect x={pad.l} y={y + (rowH - barH) / 2} width={(d.value / max) * cw} height={barH} fill={d.value >= d.target ? SEMANTIC.bandPositive : missColor} rx="2"/>
              </EntranceGroup>
              <line x1={pad.l + (d.target / max) * cw} y1={y + (rowH - trackH) / 2 - 2} x2={pad.l + (d.target / max) * cw} y2={y + (rowH + trackH) / 2 + 2} stroke={NEUTRAL.text} strokeWidth="2"/>
              {labelFmt && <text x={w - 4} y={y + rowH / 2 + 4} textAnchor="end" fontSize="11" fontWeight="700" fill={NEUTRAL.text}>{labelFmt(d.value)}</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Lollipop ─────────────────────────────────────────────────
// Redesign rule 5 — ranked style: numbered rank chip + row label +
// stem/dot in a per-rank tint degradé of the base color + bold value
// + share-of-total %. Rows render in the order given.
export function LollipopChart({ data, color, height = 200, width: widthHint = 400, dataLabels, showValues = true, format = v => v.toLocaleString(), entrance = true, ariaLabel = 'Lollipop' }) {
  const componentTheme = useColorTheme('LollipopChart');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[6 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[6 % clientColors.length];

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const max = safeMax(data.map(d => d.value)) * 1.1;
  const total = data.reduce((s, d) => s + (typeof d.value === 'number' && Number.isFinite(d.value) ? d.value : 0), 0);
  const CHIP_W = 20, CHIP_GAP = 8, LABEL_GAP = 8;
  // Adaptive label channel (mirror of HorizontalBar, PR #63): grows
  // with the longest label up to ~38% of the chart width; labels that
  // still don't fit wrap to two <tspan> lines (barLabelLayout), with
  // an end-ellipsis on line 2 + native <title> escape hatch.
  const labelMaxPx = Math.max(64, Math.round(w * 0.38) - CHIP_W - CHIP_GAP - LABEL_GAP);
  const { rows: labelRows, textW: labelW } = barLabelLayout(data.map(d => d.label), labelMaxPx);
  const padL = CHIP_W + CHIP_GAP + labelW + LABEL_GAP;
  const labelFmt = resolveLabels(dataLabels, showValues, format);
  const h = height, pad = { l: padL, r: labelFmt ? 96 : 20, t: 8, b: 8 }, cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  const n = data.length, rowH = ch / n;
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {data.map((d, i) => { const y = pad.t + (i + 0.5) * rowH, x2 = pad.l + (d.value / max) * cw;
          // Rank degradé (rule 5) from the single base color.
          const fill = tintColor(color, n <= 1 ? 0 : (i / (n - 1)) * 0.6);
          const share = total > 0 && Number.isFinite(d.value) ? (d.value / total) * 100 : null;
          return (
            <g key={i}>
              {/* Rank chip */}
              <rect x={0} y={y - CHIP_W / 2} width={CHIP_W} height={CHIP_W} rx="6" fill={NEUTRAL.track}/>
              <text x={CHIP_W / 2} y={y + 3.5} textAnchor="middle" fontSize="11" fontWeight="800" fill={NEUTRAL.muted}>{i + 1}</text>
              {/* Row label — wraps to two end-anchored tspan lines when
                  it exceeds the adaptive channel (mirror of
                  HorizontalBar); the tspan pair is centered on the
                  lollipop row (line 1 baseline above, line 2 below). */}
              {labelRows[i].wrapped ? (
                <text x={pad.l - LABEL_GAP} y={y - 2.5} textAnchor="end" fontSize="11" fill={NEUTRAL.text} fontWeight="600">
                  {labelRows[i].truncated && <title>{labelRows[i].full}</title>}
                  <tspan x={pad.l - LABEL_GAP} dy="0">{labelRows[i].lines[0]}</tspan>
                  <tspan x={pad.l - LABEL_GAP} dy="11">{labelRows[i].lines[1]}</tspan>
                </text>
              ) : (
                <text x={pad.l - LABEL_GAP} y={y + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.text} fontWeight="600">{labelRows[i].lines[0]}</text>
              )}
              <EntranceGroup mode="growX" origin={`${pad.l}px 0px`} disabled={!entrance} drawn={chartDrawn}>
                <line x1={pad.l} y1={y} x2={x2} y2={y} stroke={fill} strokeWidth="2.5" strokeLinecap="round" opacity="0.7"/>
                <circle cx={x2} cy={y} r="5.5" fill={fill}/>
              </EntranceGroup>
              {labelFmt && <text x={w - (share != null ? 44 : 6)} y={y + 4} textAnchor="end" fontSize="12" fontWeight="700" fill={NEUTRAL.text}>{labelFmt(d.value)}</text>}
              {labelFmt && share != null && <text x={w - 4} y={y + 4} textAnchor="end" fontSize="11" fontWeight="600" fill={NEUTRAL.muted}>{share.toFixed(1)}%</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Combo Chart (bar + line, dual axis) ──────────────────────
// Rule 6: monotone-cubic line half. Rule 1: bar labels (thinned) +
// line values at the extremes. Rule 3: legend-table with last value
// + delta chip per series. Mirror of `ComboChartT` minus hover.
export function ComboChart({ data, barKey = 'bar', lineKey = 'line', barLabel = 'Barras', lineLabel = 'Línea', barColor, lineColor, height = 220, width: widthHint = 400, dataLabels, showValues = true, axisYFormat, lineSuffix = '', entrance = true, ariaLabel = 'Barras + línea' }) {
  const componentTheme = useColorTheme('ComboChart');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) barColor = componentTheme.palette[0 % componentTheme.palette.length];
  if (barColor === undefined) barColor = clientColors[0 % clientColors.length];
  if (componentTheme.override) lineColor = componentTheme.palette[2 % componentTheme.palette.length];
  if (lineColor === undefined) lineColor = clientColors[2 % clientColors.length];

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  // Entrance: the line half draws in; hooks stay unconditional (top).
  const lineDraw = useLineDraw(!entrance, lineDataSig(data));
  // Unique per instance (audit #211): `url(#id)` resolves to the FIRST match
  // in the document, so a fixed id makes a 2nd chart borrow the 1st one's.
  const gid = useId().replace(/[^\w-]/g, '');
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const barVals = data.map(d => d[barKey] || 0), lineVals = data.map(d => d[lineKey] || 0);
  // Round ticks on the bar axis; the line keeps its own tight scale
  // (its right-side labels render at the shared gridlines, rounded).
  const { ticks: yTicks, max: barMax } = niceTicks(safeMax(barVals));
  // Pad AWAY from the data on each side (audit #212): `min*0.92` pulled a
  // NEGATIVE minimum toward 0 and the lowest point fell under the baseline.
  // Positive-only data keeps the exact old domain; all-zero keeps 0..1.08.
  const lineLo = safeMin(lineVals);
  const lineHi = lineLo < 0 ? lineVals.reduce((m, v) => (Number.isFinite(v) && v > m ? v : m), lineLo) : safeMax(lineVals);
  const lineMax = lineHi > 0 ? lineHi * 1.08 : lineHi * 0.92, lineMin = lineLo < 0 ? lineLo * 1.08 : lineLo * 0.92;
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  const labelFmt = resolveLabels(dataLabels, showValues);
  const labelIdx = labelFmt ? thinLabelIndices(barVals) : null;
  const fmtLineVal = v => `${yAxisFmt(v)}${lineSuffix}`;
  const barLast = lastDelta(barVals);
  const lineLast = lastDelta(lineVals);
  const legendItems = [
    { name: barLabel, color: barColor, value: barLast.value != null ? yAxisFmt(barLast.value) : null, delta: barLast.delta },
    { name: lineLabel, color: lineColor, shape: 'line', value: lineLast.value != null ? fmtLineVal(lineLast.value) : null, delta: lineLast.delta },
  ];
  const lgd = legendLayout(legendItems, Math.max(120, w - 16));
  const h = height, pad = { l: 50, r: 50, t: 14, b: 26 + lgd.height }, cw = w - pad.l - pad.r, ch = h - pad.t - pad.b, n = data.length;
  const bw = Math.min(cw / n * 0.5, 30);
  const barRx = Math.min(6, Math.max(2, bw / 2));
  // Band scale, matching the bars: the dot must sit on ITS bar's center
  // (edge-to-edge xRatio drifted right of each bar as i grew).
  const lx = i => pad.l + (i + 0.5) * (cw / n);
  const ly = v => pad.t + (1 - (v - lineMin) / (lineMax - lineMin || 1)) * ch;
  const lineD = monotonePath(data.map((d, i) => ({ x: lx(i), y: ly(d[lineKey] || 0) })));
  const labelStride = xLabelStride(data.map(d => d.label), cw, 6);
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {yTicks.map(v => { const f = barMax > 0 ? v / barMax : 0; return (
          <g key={v}>
            <line x1={pad.l} x2={w - pad.r} y1={pad.t + ch * (1 - f)} y2={pad.t + ch * (1 - f)} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l - 6} y={pad.t + ch * (1 - f) + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text>
            <text x={w - pad.r + 6} y={pad.t + ch * (1 - f) + 4} fontSize="11" fill={lineColor} opacity="0.65">{Math.round(lineMin + (lineMax - lineMin) * f)}{lineSuffix}</text>
          </g>
        ); })}
        {data.map((d, i) => {
          const bh = (d[barKey] || 0) / barMax * ch, x = pad.l + (i + 0.5) * (cw / n) - bw / 2;
          return (
            <g key={i}>
              <defs><linearGradient id={`${gid}-cc${i}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={barColor} stopOpacity="0.9"/><stop offset="100%" stopColor={barColor} stopOpacity="0.45"/></linearGradient></defs>
              <EntranceGroup mode="growY" origin={`0px ${pad.t + ch}px`} disabled={!entrance} drawn={chartDrawn}>
                <rect x={x} y={pad.t + ch - bh} width={bw} height={bh} fill={`url(#${gid}-cc${i})`} rx={barRx}/>
              </EntranceGroup>
              {labelFmt && labelIdx.has(i) && <text x={x + bw / 2} y={pad.t + ch - bh - 4} textAnchor="middle" fontSize="11" fontWeight="700" fill={NEUTRAL.text}>{labelFmt(d[barKey] || 0)}</text>}
              {i % labelStride === 0 && <text x={x + bw / 2} y={pad.t + ch + 16} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint}>{d.label}</text>}
            </g>
          );
        })}
        <path {...lineDraw} d={lineD} fill="none" stroke={lineColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
          {/* Rule 2: solid density-adaptive dots (extremes emphasized) */}
          {data.map((d, i) => <circle key={i} cx={lx(i)} cy={ly(d[lineKey] || 0)} r={lineDotRadius(n, i === 0 || i === n - 1)} fill={lineColor}/>)}
          {/* Rule 1: line values at the extremes, in the line color */}
          {labelFmt && [0, n - 1].filter((v, ix, arr) => arr.indexOf(v) === ix).map(i => (
            <text key={`ll${i}`} x={lx(i)} y={Math.max(pad.t + 10, ly(data[i][lineKey] || 0) - 9)} textAnchor={i === 0 ? 'start' : 'end'} fontSize="11" fontWeight="700" fill={lineColor}>{fmtLineVal(data[i][lineKey] || 0)}</text>
          ))}
        </EntranceGroup>
        {/* Legend-table (rule 3) */}
        <SVGLegendRows layout={lgd} width={w} top={h - lgd.height} />
      </svg>
    </div>
  );
}

// ── Bar + Trend Line ──────────────────────────────────────────
// Rule 1: bar labels ON default (thinned). The trend line is a
// linear regression — straight by definition, so rule 6 (monotone
// smoothing) does not apply to it.
export function BarTrend({ data, color, trendColor, height = 220, width: widthHint = 400, dataLabels, showValues = true, axisYFormat, entrance = true, ariaLabel = 'Barras + tendencia' }) {
  const componentTheme = useColorTheme('BarTrend');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];
  if (componentTheme.override) trendColor = componentTheme.palette[3 % componentTheme.palette.length];
  if (trendColor === undefined) trendColor = clientColors[3 % clientColors.length];

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Unique per instance (audit #211): `url(#id)` resolves to the FIRST match
  // in the document, so a fixed id makes a 2nd chart borrow the 1st one's.
  const gid = useId().replace(/[^\w-]/g, '');
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  const labelFmt = resolveLabels(dataLabels, showValues);
  const labelIdx = labelFmt ? thinLabelIndices(data.map(d => d.value)) : null;
  // Round axis ticks; the scale max IS the top tick (≥ data max).
  const { ticks: yTicks, max } = niceTicks(safeMax(data.map(d => d.value)));
  const h = height, pad = { l: 48, r: 14, t: 14, b: 36 }, cw = w - pad.l - pad.r, ch = h - pad.t - pad.b, n = data.length;
  const bw = Math.min(cw / n * 0.6, 36);
  const xs = data.map((_, i) => i), ys = data.map(d => d.value);
  const xm = xs.reduce((a, b) => a + b, 0) / n, ym = ys.reduce((a, b) => a + b, 0) / n;
  // Linear regression denominator is 0 when n === 1 (or when all xs
  // are identical). Guard so the trend line falls back to a flat line
  // at the mean rather than poisoning everything downstream with NaN.
  const slopeDen = xs.reduce((acc, x) => acc + (x - xm) ** 2, 0);
  const slope =
    slopeDen > 0
      ? xs.reduce((acc, x, i) => acc + (x - xm) * (ys[i] - ym), 0) / slopeDen
      : 0;
  const intercept = ym - slope * xm;
  const tx = i => pad.l + (i + 0.5) * (cw / n);
  const ty = v => pad.t + (1 - v / max) * ch;
  const t0 = slope * 0 + intercept, t1 = slope * (n - 1) + intercept;
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.l} x2={w - pad.r} y1={pad.t + ch * (1 - v / max)} y2={pad.t + ch * (1 - v / max)} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l - 6} y={pad.t + ch * (1 - v / max) + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text>
          </g>
        ))}
        {data.map((d, i) => {
          const bh = d.value / max * ch, x = pad.l + (i + 0.5) * (cw / n) - bw / 2;
          return (
            <g key={i}>
              <defs><linearGradient id={`${gid}-btr${i}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.9"/><stop offset="100%" stopColor={color} stopOpacity="0.45"/></linearGradient></defs>
              <EntranceGroup mode="growY" origin={`0px ${pad.t + ch}px`} disabled={!entrance} drawn={chartDrawn}>
                <rect x={x} y={pad.t + ch - bh} width={bw} height={bh} fill={`url(#${gid}-btr${i})`} rx={Math.min(6, bw / 2)}/>
              </EntranceGroup>
              {labelFmt && labelIdx.has(i) && <text x={x + bw / 2} y={pad.t + ch - bh - 4} textAnchor="middle" fontSize="11" fontWeight="700" fill={NEUTRAL.text}>{labelFmt(d.value)}</text>}
              <SlotLabel label={d.label} slot={cw / n} x={x + bw / 2} y={h - pad.b + 14} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint} />
            </g>
          );
        })}
        <line x1={tx(0)} y1={ty(t0)} x2={tx(n - 1)} y2={ty(t1)} stroke={trendColor} strokeWidth="2.5" strokeDasharray="7,4" strokeLinecap="round" opacity="0.85"/>
        <g transform={`translate(${w - 130},${h - 10})`}>
          <line x1="0" y1="4" x2="14" y2="4" stroke={trendColor} strokeWidth="2" strokeDasharray="5,3"/>
          <text x="18" y="8" fontSize="11" fill={NEUTRAL.faint}>Trend</text>
        </g>
      </svg>
    </div>
  );
}

// ── Line + Forecast ───────────────────────────────────────────
// Rule 6: monotone-cubic on both the historical and the forecast
// segments. Rule 2: dots on the real points (extremes emphasized).
// Rule 1: labels ON default, thinned over the full sequence.
export function LineTrend({ data, forecast = [], color, height = 220, width: widthHint = 400, dataLabels, showValues = true, axisYFormat, entrance = true, ariaLabel = 'Línea + proyección' }) {
  const componentTheme = useColorTheme('LineTrend');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  // Entrance: the historical (solid) stroke draws in; the forecast
  // stroke is dashed — dash props would clobber its pattern, so it
  // only fades in with the companion marks.
  const lineDraw = useLineDraw(!entrance, lineDataSig(data));
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const allPts = [...data, ...forecast.slice(1)], allVals = allPts.map(d => d.value);
  // Round axis ticks; domain snaps to the nice floor/ceil of the data.
  const { ticks: yTicks, max, min } = niceTicks(safeMax(allVals), 4, safeMin(allVals));
  const h = height, pad = { l: 48, r: 12, t: 14, b: 30 }, cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  const N = allPts.length, nAct = data.length;
  const px = i => lineX(i, N, pad.l, cw);
  const py = v => pad.t + (1 - (v - min) / (max - min || 1)) * ch;
  const actD = monotonePath(data.map((d, i) => ({ x: px(i), y: py(d.value) })));
  const fcD = monotonePath([data[nAct - 1], ...forecast.slice(1)].map((d, i) => ({ x: px(nAct - 1 + i), y: py(d.value) })));
  const actAreaD = `${actD}L${px(nAct - 1).toFixed(2)},${(pad.t + ch).toFixed(2)}L${px(0).toFixed(2)},${(pad.t + ch).toFixed(2)}Z`;
  const fcAreaD = `${fcD}L${px(N - 1).toFixed(2)},${(pad.t + ch).toFixed(2)}L${px(nAct - 1).toFixed(2)},${(pad.t + ch).toFixed(2)}Z`;
  const fcColor = PALETTE[2];
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  const labelFmt = resolveLabels(dataLabels, showValues);
  const labelIdx = labelFmt ? thinLabelIndices(allVals) : null;
  return (
    <div ref={wrapRef} style={{ height: h }} className="w-full overflow-hidden">
      <svg width={w} height={h} role="img" aria-label={ariaLabel}>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.l} x2={w - pad.r} y1={py(v)} y2={py(v)} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l - 6} y={py(v) + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text>
          </g>
        ))}
        <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
          <path d={actAreaD} fill={color} opacity="0.08"/>
          {forecast.length > 1 && <path d={fcAreaD} fill={fcColor} opacity="0.07"/>}
        </EntranceGroup>
        <path {...lineDraw} d={actD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
          {forecast.length > 1 && <path d={fcD} fill="none" stroke={fcColor} strokeWidth="2.5" strokeDasharray="7,4" strokeLinecap="round" strokeLinejoin="round" opacity="0.8"/>}
        </EntranceGroup>
        {forecast.length > 1 && <line x1={px(nAct - 1)} y1={pad.t} x2={px(nAct - 1)} y2={pad.t + ch} stroke={NEUTRAL.axis} strokeWidth="1" strokeDasharray="4,3"/>}
        <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
          {data.map((d, i) => <circle key={i} cx={px(i)} cy={py(d.value)} r={lineDotRadius(N, i === 0 || i === N - 1)} fill={color}/>)}
          {forecast.slice(1).map((d, i) => <circle key={i} cx={px(nAct + i)} cy={py(d.value)} r={lineDotRadius(N, nAct + i === N - 1)} fill={fcColor} opacity="0.85"/>)}
          {labelFmt && allPts.map((d, i) => labelIdx.has(i) && (
            <text key={`lbl${i}`} x={px(i)} y={Math.max(pad.t + 10, py(d.value) - 9)} textAnchor="middle" fontSize="11" fontWeight="700" fill={i >= nAct ? fcColor : color}>{labelFmt(d.value)}</text>
          ))}
        </EntranceGroup>
        {allPts.map((d, i) => i % xLabelStride(allPts.map(x => x.label), cw, 8) === 0 && (
          <text key={i} x={px(i)} y={h - pad.b + 14} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint}>{d.label}</text>
        ))}
        {forecast.length > 1 && <g transform={`translate(${w - 168},${h - 10})`}>
          <line x1="0" y1="4" x2="12" y2="4" stroke={color} strokeWidth="2"/><text x="16" y="8" fontSize="11" fill={NEUTRAL.muted}>Historical</text>
          <line x1="58" y1="4" x2="70" y2="4" stroke={fcColor} strokeWidth="2" strokeDasharray="5,3" opacity="0.8"/><text x="74" y="8" fontSize="11" fill={NEUTRAL.muted}>Forecast</text>
        </g>}
      </svg>
    </div>
  );
}

// ── Sparkline (inline mini — keeps explicit size, no resize needed) ──
export function Sparkline({ data, labels, format, color, width = 80, height = 28, entrance = true, ariaLabel = 'Sparkline' }) {
  const componentTheme = useColorTheme('Sparkline');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  // Entrance: the stroke draws in; hooks stay unconditional (top).
  const lineDraw = useLineDraw(!entrance, lineDataSig(data));
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  // Hover readout (only when the caller names the points): the nearest
  // point's label + value in a portaled CursorTip, a marker dot on the
  // line. Nearest-by-x, not hit-testing 2px dots: an 80px sparkline of
  // 30 days has a point every ~2.8px, so the target is the column,
  // not the dot.
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  if (!hasData(data)) return <svg width={width} height={height} aria-hidden="true"/>;
  const hasLabels = Array.isArray(labels) && labels.length === data.length;
  const fmtV = typeof format === 'function' ? format : v => v.toLocaleString();
  const max = safeMax(data), min = safeMin(data), range = max - min || 1;
  // Inset: the endpoint dot (r 2.6 + 1px ring) and the hover marker
  // (r 3.2 + 2px ring) are drawn ON the last point, and the last point
  // used to sit at x = width / y = height - 2 — so the svg viewport cut
  // both in half (JP, 2026-09-24). 5px keeps the biggest marker whole.
  const PAD = 5;
  const innerW = Math.max(1, width - 2 * PAD), innerH = Math.max(1, height - 2 * PAD);
  // Guard the X step against single-point sparkline data — `(data.length
  // - 1)` is 0 when data has only 1 point, which used to produce
  // `<polyline points="NaN,30">` SVG warnings. Single-point sparklines
  // now place the dot in the middle of the box.
  const xStep = data.length > 1 ? innerW / (data.length - 1) : 0;
  const px = i => (data.length > 1 ? PAD + i * xStep : width / 2);
  const py = v => PAD + (1 - (v - min) / range) * innerH;
  // Rule 6: monotone-cubic smoothing, same helper as the full-size
  // line charts (also covers KPISparkline, which renders this).
  const pts = data.map((v, i) => ({ x: px(i), y: py(v) }));
  const lastX = px(data.length - 1);
  const lastY = py(data[data.length - 1]);
  const onMove = hasLabels
    ? (e) => {
        const r = svgRef.current?.getBoundingClientRect();
        if (!r || r.width === 0) return;
        const relX = ((e.clientX - r.left) / r.width) * width;
        const i = data.length > 1 ? Math.max(0, Math.min(data.length - 1, Math.round((relX - PAD) / xStep))) : 0;
        setHover({ i, x: e.clientX, y: e.clientY });
      }
    : undefined;
  const hp = hover ? pts[hover.i] : null;
  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className="inline-block"
      role="img"
      aria-label={ariaLabel}
      onMouseMove={onMove}
      onMouseLeave={hasLabels ? () => setHover(null) : undefined}
      data-hoverable={hasLabels ? '' : undefined}
    >
      {/* Rule 2/6: 2.5px monotone stroke + emphasized endpoint dot */}
      <path {...lineDraw} d={monotonePath(pts)} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
        <circle cx={lastX} cy={lastY} r="2.6" fill={color} stroke="white" strokeWidth="1"/>
      </EntranceGroup>
      {hp && (
        <>
          <line x1={hp.x} x2={hp.x} y1={0} y2={height} stroke={NEUTRAL.axis} strokeWidth="1" strokeDasharray="2 2"/>
          <circle data-hover-dot="" cx={hp.x} cy={hp.y} r="3.2" fill="white" stroke={color} strokeWidth="2"/>
        </>
      )}
      {hp && (
        <CursorTip x={hover.x} y={hover.y}>
          <span className="text-neutral-300">{labels[hover.i]}</span>
          <span className="ml-1.5 font-bold tabular-nums">{fmtV(data[hover.i])}</span>
        </CursorTip>
      )}
    </svg>
  );
}

// ── Progress Ring (inline — keeps explicit size, no resize needed) ──
export function ProgressRing({ value, max = 100, size = 64, strokeWidth = 6, color, label, entrance = true }) {
  const componentTheme = useColorTheme('ProgressRing');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  // Entrance (ronda 12): the ring sweeps from empty (full-circumference
  // offset) to its value via the existing dashoffset — compositor-only.
  const entered = useEntrance();
  const reducedMotion = usePrefersReducedMotion();
  // Geometry clamped to [0, 1] (audit #218): 130 of 100 is a full ring,
  // never a wrapped one; the label keeps the real value.
  const ratio = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  const r = (size - strokeWidth) / 2, circumference = 2 * Math.PI * r, offset = circumference - ratio * circumference;
  const animating = entrance && !reducedMotion;
  return (
    <div className="inline-flex flex-col items-center gap-1">
      <svg width={size} height={size} className="transform -rotate-90" role="img" aria-label={`${Math.round(value)} de ${max}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={NEUTRAL.track} strokeWidth={strokeWidth}/>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={animating && !entered ? circumference : offset} strokeLinecap="round" style={animating ? { transition: `stroke-dashoffset ${MOTION.durEntrance}ms ${MOTION.easeOut}` } : undefined}/>
      </svg>
      <span className="text-xs font-bold text-neutral-800" style={{ marginTop: -size / 2 - 5, position: 'relative' }}>{Math.round(value)}%</span>
      {label && <span className="text-2xs text-neutral-600 mt-3">{label}</span>}
    </div>
  );
}

// ── StackedShareBar (epic v3 — executive scorecards) ─────────
//
// A single-row 100%-normalized horizontal stacked bar: one row per
// entity, segments in the caller's declared order, no gaps. The one
// primitive the client's mockups needed that the catalog genuinely
// lacked — `StackedBar` is a multi-row categorical chart with axes,
// `KPIBar` is a single-value progress track, and neither renders "this
// row's composition adds up to 100%".
//
// Missing-segment policy is the load-bearing decision. In a NORMALIZED
// bar, dropping a segment redistributes its share across the survivors
// exactly as surely as coercing it to 0 does — both fabricate a share
// nobody measured. So the default is to refuse: `onMissing: 'block'`
// renders the empty state naming the offending key. `'rebase'` is the
// explicit opt-in for "the remaining buckets really are the whole
// universe".

/**
 * Pure share math for `StackedShareBar` — exported so the decisions
 * (blocking, rebasing, the zero-sum guard) are unit-testable without a
 * DOM. Returns `{ ok, error, missingKey, rows }`; `rows` carry a
 * `share` in [0, 1] that sums to 1 when `ok`.
 */
export function normalizeSegments(segments, { normalize = true, onMissing = 'block' } = {}) {
  const list = Array.isArray(segments) ? segments : [];
  if (list.length === 0) return { ok: false, error: 'empty', missingKey: null, rows: [] };

  const missing = list.find(
    (s) => !s || typeof s.value !== 'number' || !Number.isFinite(s.value),
  );
  if (missing && onMissing !== 'rebase') {
    return {
      ok: false,
      error: 'missing-segment',
      missingKey: (missing && missing.key) || null,
      rows: [],
    };
  }

  const usable = list.filter(
    (s) => s && typeof s.value === 'number' && Number.isFinite(s.value) && s.value >= 0,
  );
  const total = usable.reduce((acc, s) => acc + s.value, 0);
  // Every bucket zero is not "an equal split" — it is no data. Without
  // this guard the first segment would fill the whole bar (0/0).
  if (!(total > 0)) return { ok: false, error: 'zero-total', missingKey: null, rows: [] };

  const rows = usable.map((s) => ({
    key: s.key,
    label: s.label,
    color: s.color,
    value: s.value,
    share: normalize ? s.value / total : Math.max(0, Math.min(1, s.value / 100)),
  }));
  return { ok: true, error: null, missingKey: null, rows };
}

/** Give every non-zero share at least `minPx`, taking the difference
 *  from the segments that can afford it, so a 0.4% bucket stays
 *  visible without the bar overflowing its width. */
function widthsWithMinimum(shares, totalPx, minPx) {
  const raw = shares.map((s) => s * totalPx);
  const needsBump = raw.map((px, i) => px > 0 && px < minPx && shares[i] > 0);
  const bumpCount = needsBump.filter(Boolean).length;
  if (bumpCount === 0) return raw;
  const bumped = bumpCount * minPx;
  const donorTotal = raw.reduce((acc, px, i) => (needsBump[i] ? acc : acc + px), 0);
  const room = totalPx - bumped;
  if (!(donorTotal > 0) || !(room > 0)) return raw;
  return raw.map((px, i) => (needsBump[i] ? minPx : (px / donorTotal) * room));
}

export function StackedShareBar({
  segments,
  height = 14,
  rounded = true,
  normalize = true,
  minSegmentPx = 2,
  onMissing = 'block',
  format,
  ariaLabel = 'Share breakdown',
  onSegmentHover,
  width: widthHint = 400,
  className = '',
  entrance = true,
}) {
  const [wrapRef, w] = useContainerWidth(widthHint);
  // Interactive breakdown on hover (2026-07 CHS feedback): the bar is
  // tiny in card rows, so the cursor is the only room the values have.
  // PORTALED CursorTip (ronda 5): an in-card tooltip either got
  // clipped or grew the card-grid scroller (double scrollbar), and the
  // native <title> doubled the readout with the browser's own tooltip —
  // aria-label keeps the screen-reader channel without the visual dupe.
  const [hover, setHover] = useState(null); // {key, x, y, content}
  // Entrance (ronda 12): the whole bar grows left->right from zero on
  // first paint (compositor-only scaleX on the segments group).
  const entered = useEntrance();
  const reducedMotion = usePrefersReducedMotion();
  const result = normalizeSegments(segments, { normalize, onMissing });

  if (!result.ok) {
    const message =
      result.error === 'missing-segment'
        ? `Sin dato para "${result.missingKey ?? '—'}"`
        : result.error === 'zero-total'
          ? 'Sin datos disponibles'
          : 'Sin datos disponibles';
    return (
      <div ref={wrapRef} className={className}>
        <ChartEmpty height={Math.max(height + 12, 32)} message={message} />
      </div>
    );
  }

  const fmt = typeof format === 'function' ? format : (v) => `${(v * 100).toFixed(1)}%`;
  const widths = widthsWithMinimum(result.rows.map((r) => r.share), w, minSegmentPx);
  const r = rounded ? Math.min(height / 2, 7) : 0;
  let x = 0;

  return (
    <div ref={wrapRef} className={className}>
      <svg width={w} height={height} role="img" aria-label={ariaLabel}>
        {/* Outer rounded ends without rounding every internal seam:
            one clip path over the whole bar, plain rects inside. */}
        <defs>
          <clipPath id={`pm-share-clip-${Math.round(w)}-${height}`}>
            <rect x="0" y="0" width={Math.max(w, 0)} height={height} rx={r} ry={r} />
          </clipPath>
        </defs>
        <g
          clipPath={`url(#pm-share-clip-${Math.round(w)}-${height})`}
          style={{
            transform: entrance && !entered ? 'scaleX(0)' : 'none',
            transformOrigin: '0px 50%',
            transition: reducedMotion || !entrance ? undefined : `transform ${MOTION.durEntrance}ms ${MOTION.easeOut}`,
          }}
        >
          {result.rows.map((row, i) => {
            const segW = widths[i];
            const rect = (
              <rect
                key={row.key ?? i}
                x={x}
                y={0}
                width={Math.max(0, segW)}
                height={height}
                fill={row.color || PALETTE[i % PALETTE.length]}
                opacity={hover !== null && hover.key !== row.key ? 0.45 : 1}
                // aria-label (NOT <title>): the screen-reader channel
                // without the browser's native tooltip doubling the
                // CursorTip readout.
                aria-label={`${row.label ?? row.key}: ${fmt(row.share)}`}
                onMouseMove={(e) => {
                  setHover({
                    key: row.key,
                    x: e.clientX,
                    y: e.clientY,
                    content: `${row.label ?? row.key} · ${fmt(row.share)} · ${fmtN(row.value)}`,
                  });
                  if (onSegmentHover) onSegmentHover(row.key, e);
                }}
                onMouseLeave={() => {
                  setHover(null);
                  if (onSegmentHover) onSegmentHover(null);
                }}
              />
            );
            x += segW;
            return rect;
          })}
        </g>
      </svg>
      {hover && <CursorTip x={hover.x} y={hover.y}>{hover.content}</CursorTip>}
    </div>
  );
}

// ── IndexMeter (Pricing-Brand migration, 2026-09-08) ─────────
//
// "Where does ONE value sit on a scale that has a reference point and
// a plausible band?" — the price-index card asks exactly that: 92 on a
// 70–130 scale, market average pinned at 100, the P25–P75 band (85–104)
// shaded behind it.
//
// Nothing in the catalog answered it. `Gauge` is an arc with no band
// and no reference; `BulletChart` has a target tick but its "band" is
// a second bar growing from zero, so it cannot express a band that
// does NOT start at the axis origin — which is the whole point of a
// percentile band. `KPIBar` is a 0→value progress track.
//
// Colour is never the only channel (Pablo is red-green colour-blind):
// the reference is a DASHED line with its number printed, the band is
// bracketed by two tick marks AND labelled in text, and the value is a
// diamond on a stem — three different SHAPES.
//
// Fail-loud: a non-finite value, a non-finite scale or an inverted
// scale renders the empty state naming the problem instead of drawing
// a marker at an arbitrary position. A value OUTSIDE the scale is not
// an error (an index can legitimately blow past the axis) — it is
// pinned to the edge and flagged with a caret, and the printed number
// is always the real one.
//
// Delta (Pricing-Brand G-3, 2026-10-19): `delta` is the change of the
// index in POINTS against a previous period the caller names in
// `deltaLabel` ("vs Q1", "vs semana anterior"). It renders as the shared
// `DeltaChip` beside the headline. Direction alone says nothing about an
// index — for a price index "down" means cheaper, for a share index
// "down" means losing — so the colour comes ONLY from an explicit
// `deltaGoodWhen` ('up' | 'down'); without it the chip is neutral grey
// with its arrow. A sentiment is never inferred from the sign here.
export function IndexMeter({
  value,
  min,
  max,
  band = null,
  reference = null,
  unit = '',
  note = '',
  delta = null,
  deltaLabel = '',
  deltaGoodWhen = null,
  minLabel = '',
  maxLabel = '',
  format = (v) => String(Math.round(v * 10) / 10),
  color = PALETTE[0],
  height = 132,
  width: widthHint = 400,
  entrance = true,
  ariaLabel = 'Índice',
}) {
  const [wrapRef, w] = useContainerWidth(widthHint);
  const drawn = useGrowCycle(useMemo(() => lineDataSig(value), [value]), !entrance);
  const finite = (n) => typeof n === 'number' && Number.isFinite(n);
  if (!finite(value) || !finite(min) || !finite(max) || !(max > min)) {
    const why = !finite(value)
      ? 'Sin valor de índice'
      : !finite(min) || !finite(max)
        ? 'Escala sin límites'
        : 'Escala invertida (min ≥ max)';
    return <div ref={wrapRef}><ChartEmpty height={height} message={why} /></div>;
  }

  const PAD = 10;
  const trackW = Math.max(0, w - PAD * 2);
  const at = (v) => PAD + ((Math.min(max, Math.max(min, v)) - min) / (max - min)) * trackW;
  const below = value < min, above = value > max;
  const TRACK_H = 10;
  // `ty` tiene que dejar lugar ARRIBA para el label de la referencia,
  // que se dibuja por encima del track. Con ty=12 el baseline caia en 4
  // y un glifo de 9.5px arranca ~7px mas arriba: salia del SVG y el
  // numero aparecia cortado (reportado en stg: el "100" invisible).
  // REF_GAP es ese hueco, y SVG_H se deriva en vez de ser una constante
  // suelta que hay que acordarse de mover. Con el piso de 11 px (QA F16)
  // la caja del texto (ascendente de la fuente, no solo el glifo) sube
  // ~11px sobre el baseline: medido en stg, con 13 empezaba 2px arriba
  // del SVG. 15 la deja entera adentro.
  const REF_GAP = 15;
  const ty = REF_GAP + 4;
  const SVG_H = ty + TRACK_H + 8;
  const vx = at(value);
  const bandOk = band && finite(band.from) && finite(band.to) && band.to > band.from;
  const refOk = reference && finite(reference.value);
  const hasDelta = finite(delta);
  // Sign-aware colour ONLY under an explicit direction; otherwise neutral.
  const deltaSentiment = !hasDelta || delta === 0
    ? 'neutral'
    : deltaGoodWhen === 'down'
      ? (delta < 0 ? 'good' : 'bad')
      : deltaGoodWhen === 'up'
        ? resolveSentiment(undefined, delta)
        : 'neutral';
  const deltaText = hasDelta
    ? `${delta > 0 ? '+' : ''}${format(delta)}${deltaLabel ? ` ${deltaLabel}` : ''}`
    : null;

  return (
    <div ref={wrapRef} className="w-full" style={{ minHeight: height }}>
      <div className="flex items-baseline gap-2">
        <span className="font-display text-3xl font-extrabold leading-none tracking-[-0.025em] tabular-nums" style={{ color: NEUTRAL.text }}>
          {format(value)}
        </span>
        {unit && <span className="text-xs font-medium" style={{ color: NEUTRAL.muted }}>{unit}</span>}
        {hasDelta && (
          <DeltaChip
            delta={delta}
            label={deltaText}
            sentiment={deltaSentiment}
            entrance={entrance}
            className="ml-auto shrink-0"
          />
        )}
      </div>
      {note && (
        <p className="mt-1.5 text-[11px] leading-snug" style={{ color: NEUTRAL.muted }}>{note}</p>
      )}
      <svg className="mt-2.5" width={w} height={SVG_H} role="img"
           aria-label={`${ariaLabel}: ${format(value)} en escala ${format(min)}–${format(max)}`}>
        <rect x={PAD} y={ty} width={trackW} height={TRACK_H} rx={TRACK_H / 2} fill={NEUTRAL.track} />
        {bandOk && (
          <>
            <rect x={at(band.from)} y={ty} width={Math.max(1, at(band.to) - at(band.from))}
                  height={TRACK_H} rx={2} fill={tintColor(color, 0.74)} />
            {/* Band edges as TICKS: the shaded fill alone is a hue, and
                a hue is exactly the channel that is unavailable to a
                deuteranope. */}
            {[band.from, band.to].map((b, i) => (
              <line key={i} x1={at(b)} y1={ty - 3} x2={at(b)} y2={ty + TRACK_H + 3}
                    stroke={NEUTRAL.axis} strokeWidth="1.5" />
            ))}
          </>
        )}
        {refOk && (
          <>
            <line x1={at(reference.value)} y1={ty - 5} x2={at(reference.value)} y2={ty + TRACK_H + 5}
                  stroke={NEUTRAL.text} strokeWidth="1.5" strokeDasharray="3 2" />
            <text x={at(reference.value)} y={ty - 8} textAnchor="middle" fontSize="11"
                  fontWeight="700" fill={NEUTRAL.muted}>
              {reference.label != null ? String(reference.label) : format(reference.value)}
            </text>
          </>
        )}
        <EntranceGroup mode="fade" disabled={!entrance} drawn={drawn}>
          <g>
            <line x1={vx} y1={ty - 2} x2={vx} y2={ty + TRACK_H + 2} stroke={NEUTRAL.text} strokeWidth="2" />
            {/* Diamond, not a dot: the value marker has to be
                distinguishable from the reference tick by SHAPE. */}
            <path d={`M${vx},${ty + TRACK_H / 2 - 6} L${vx + 5.5},${ty + TRACK_H / 2} L${vx},${ty + TRACK_H / 2 + 6} L${vx - 5.5},${ty + TRACK_H / 2} Z`}
                  fill={color} stroke="white" strokeWidth="1.5" />
            {(below || above) && (
              <text x={below ? vx + 9 : vx - 9} y={ty + TRACK_H / 2 + 3.5}
                    textAnchor={below ? 'start' : 'end'} fontSize="11" fontWeight="800" fill={color}>
                {below ? '‹' : '›'}
              </text>
            )}
          </g>
        </EntranceGroup>
      </svg>
      <div className="flex items-baseline justify-between text-2xs" style={{ color: NEUTRAL.faint }}>
        <span>{minLabel || format(min)}</span>
        <span>{maxLabel || format(max)}</span>
      </div>
      {bandOk && band.label && (
        <p className="mt-1 text-2xs" style={{ color: NEUTRAL.muted }}>{band.label}</p>
      )}
    </div>
  );
}

// ── ShareBreakdown (Pricing-Brand migration, 2026-09-08) ─────
//
// A 100%-stacked bar ABOVE its own legend table: swatch · state ·
// "n de N". `StackedShareBar` draws the bar and nothing else, so every
// caller that needed the reading had to hand-build a legend beside it
// (which is how `metric_card_grid` ended up with a chip row that shows
// colours but not counts). This composes the two, keeps ONE declared
// segment order feeding both, and prints the count inside any segment
// wide enough to hold it.
//
// It reuses `normalizeSegments`, so it inherits the missing-bucket
// policy verbatim: a segment with no measured value blocks the whole
// bar rather than letting the survivors silently absorb its share.
//
// `total` is a CHECK, not a source: pass it and a mismatch against the
// summed segments renders an error naming both numbers. Two different
// counts of the same universe is a data bug, and the one thing worse
// than showing it is picking one.
// Legend that filters (DSA 1.11): `onSegmentClick(key)` makes every
// legend row (and bar segment) a button; `activeKey` marks the selected
// segment — the others dim so the selection reads at a glance — and
// `clickHint` is the one-line affordance under the legend. Without
// `onSegmentClick` the component renders byte-identically to before.
export function ShareBreakdown({
  segments,
  total = null,
  totalLabel = '',
  ofLabel = 'de',
  barHeight = 14,
  inBarMinShare = 0.07,
  format = (v) => v.toLocaleString(),
  height = 200,
  width: widthHint = 400,
  entrance = true,
  ariaLabel = 'Distribución',
  onSegmentClick,
  activeKey,
  clickHint,
  fill = false,
}) {
  // `fill`: the component takes its PARENT's full height (the parent must
  // have one) instead of packing at the top under a `maxHeight` — a
  // thicker bar and a legend whose rows spread over the space left. In a
  // dashboard grid cell that space is the cell's, so leaving it blank
  // under a six-row legend read as a broken card. Too short → it scrolls,
  // exactly like the packed form.
  barHeight = fill ? Math.max(barHeight, 22) : barHeight;
  const clickable = typeof onSegmentClick === 'function';
  const isDimmed = (key) => activeKey != null && activeKey !== '' && key !== activeKey;
  const [wrapRef, w] = useContainerWidth(widthHint);
  const list = Array.isArray(segments) ? segments : [];
  const drawn = useGrowCycle(
    useMemo(() => lineDataSig(list.map((s) => (s ? s.value : null))), [list]),
    !entrance,
  );
  const result = normalizeSegments(list, { normalize: true, onMissing: 'block' });
  if (!result.ok) {
    const message =
      result.error === 'missing-segment'
        ? `Sin dato para "${result.missingKey ?? '—'}"`
        : 'Sin datos disponibles';
    return <div ref={wrapRef}><ChartEmpty height={height} message={message} /></div>;
  }
  const sum = result.rows.reduce((acc, r) => acc + r.value, 0);
  if (typeof total === 'number' && Number.isFinite(total) && Math.round(total) !== Math.round(sum)) {
    return (
      <div ref={wrapRef}>
        <ChartEmpty height={height}
          message={`Total declarado ${format(total)} ≠ suma de segmentos ${format(sum)}`} />
      </div>
    );
  }
  const widths = widthsWithMinimum(result.rows.map((r) => r.share), w, 2);
  const rx = Math.min(barHeight / 2, 7);
  let x = 0;

  return (
    <div ref={wrapRef}
         className={fill ? 'flex h-full w-full flex-col overflow-y-auto' : 'w-full overflow-y-auto'}
         style={fill ? undefined : { maxHeight: height }}>
      <svg className="shrink-0" width={w} height={barHeight} role="img" aria-label={ariaLabel}>
        <defs>
          <clipPath id={`pm-sbd-clip-${Math.round(w)}-${barHeight}`}>
            <rect x="0" y="0" width={Math.max(w, 0)} height={barHeight} rx={rx} ry={rx} />
          </clipPath>
        </defs>
        <EntranceGroup mode="growX" origin="0px 50%" disabled={!entrance} drawn={drawn}>
          <g clipPath={`url(#pm-sbd-clip-${Math.round(w)}-${barHeight})`}>
            {result.rows.map((row, i) => {
              const segW = widths[i];
              const cx = x + segW / 2;
              const g = (
                <g key={row.key ?? i}
                   onClick={clickable ? () => onSegmentClick(row.key) : undefined}
                   style={clickable ? { cursor: 'pointer' } : undefined}
                   opacity={isDimmed(row.key) ? 0.35 : 1}>
                  <rect x={x} y={0} width={Math.max(0, segW)} height={barHeight}
                        fill={row.color || PALETTE[i % PALETTE.length]}
                        aria-label={`${row.label ?? row.key}: ${format(row.value)}`} />
                  {/* The count rides INSIDE the segment when it fits —
                      the reference card prints exactly the two buckets
                      that dominate the bar, and reading them off the
                      legend costs a saccade. */}
                  {row.share >= inBarMinShare && (
                    <text x={cx} y={barHeight / 2 + (fill ? 4 : 3.5)} textAnchor="middle" fontSize={11}
                          fontWeight="800" fill="white">{format(row.value)}</text>
                  )}
                </g>
              );
              x += segW;
              return g;
            })}
          </g>
        </EntranceGroup>
      </svg>
      <ul className={fill ? 'mt-2.5 flex flex-1 flex-col justify-evenly' : 'mt-1.5 space-y-px'}>
        {result.rows.map((row, i) => {
          const active = activeKey != null && activeKey !== '' && row.key === activeKey;
          const Row = clickable ? 'button' : 'div';
          return (
            <li key={row.key ?? i} className={fill ? 'border-b last:border-b-0' : undefined}
                style={fill ? { borderColor: NEUTRAL.border } : undefined}>
              <Row
                type={clickable ? 'button' : undefined}
                onClick={clickable ? () => onSegmentClick(row.key) : undefined}
                aria-pressed={clickable ? active : undefined}
                className={`flex w-full items-center gap-2 rounded px-1 text-left leading-tight ${fill ? 'py-1 text-[12px]' : 'text-2xs'} ${clickable ? 'cursor-pointer hover:bg-neutral-50' : ''}`}
                style={{ opacity: isDimmed(row.key) ? 0.5 : 1, background: active ? NEUTRAL.track : undefined }}>
                <span className={`inline-block shrink-0 rounded-sm ${fill ? 'h-3 w-3' : 'h-2.5 w-2.5'}`}
                      style={{ background: row.color || PALETTE[i % PALETTE.length] }} aria-hidden="true" />
                <span className={`min-w-0 flex-1 truncate ${active ? 'font-bold' : ''}`} style={{ color: active ? NEUTRAL.text : NEUTRAL.muted }}>
                  {row.label ?? row.key}
                </span>
                <span className="shrink-0 font-semibold tabular-nums" style={{ color: NEUTRAL.text }}>
                  {format(row.value)}
                </span>
                <span className="shrink-0 tabular-nums" style={{ color: NEUTRAL.faint }}>
                  {ofLabel} {format(sum)}
                </span>
              </Row>
            </li>
          );
        })}
      </ul>
      {clickable && clickHint && (
        <p className="mt-1 text-2xs" style={{ color: NEUTRAL.faint }}>{clickHint}</p>
      )}
      {totalLabel && (
        <div className="mt-1.5 flex items-center justify-between border-t pt-1 text-2xs font-semibold"
             style={{ borderColor: NEUTRAL.border, color: NEUTRAL.text }}>
          <span>{totalLabel}</span>
          <span className="tabular-nums">{format(sum)}</span>
        </div>
      )}
    </div>
  );
}
